import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormArray,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { ConfirmDialogComponent } from 'app/components/confirm-dialog/confirm-dialog.component';
import { AuthService } from 'app/core/auth/auth.service';
import { environment } from 'environments/environment';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  firstValueFrom,
  forkJoin,
  finalize,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';

import { AccountsService } from '@modules/accounts/accounts.service';
import { Account } from '@modules/accounts/accounts.types';
import { BotCrudService } from '@modules/bots/components/bots-crud/bots-crud.service';
import {
  BotData,
  ChatbotCustomization,
  FollowUpConfiguration,
  MileageCollection,
  WelcomeSyncResult,
} from '@modules/bots/components/bots-crud/bots-crud.types';
import { SubAccountsService } from '@modules/sub-accounts/sub-accounts.service';
import { SubAccount } from '@modules/sub-accounts/sub-accounts.types';
import { formatOfficeScheduleSummary, BUSINESS_HOURS_SETTINGS_SECTION } from 'app/shared/utils/office-schedule.utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TagDefinition, TaggingRule, TaggingRulePayload } from 'app/interfaces/tagging';

import { BotSetupStateService } from './bot-setup-state.service';
import { BotSetup, BotSetupFormState, Origin } from './bot-setup.model';
import {
  VoiceAttributeLevel,
  VoiceFlavorPreset,
  FALLBACK_VOICE_FLAVOR_PRESETS,
  voiceAttributeLevelFromValue,
  voiceAttributeScore,
  VoiceFlavorSetupPayload,
} from './voice-flavor.types';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ChatbotCustomizationComponent } from './chatbot-customization/chatbot-customization.component';
import { VoiceCatalogComponent } from './voice-catalog/voice-catalog.component';
import { VoiceCatalogService } from './voice-catalog/voice-catalog.service';
import { DEFAULT_FLUX_VOICE, FLUX_VOICE_OPTIONS, FluxVoice } from './flux-voices';
import { DEFAULT_REALTIME_VOICE, REALTIME_VOICE_OPTIONS, RealtimeVoice } from './realtime-voices';

const STEP_1_GROUP = 'step1';
const STEP_2_GROUP = 'step2';
const STEP_8_GROUP = 'step8';
const STEP_2_TYPE_CONTROL = `${STEP_2_GROUP}.type`;
const SUB_ACCOUNT_CONTROL = 'step1.subAccount';
const ACCOUNT_SEARCH_OPTION_VALUE = '__SEARCH_PARTNERS__';
const SUB_ACCOUNT_SEARCH_OPTION_VALUE = '__SEARCH_SUB_ACCOUNTS__';
const SEARCH_DEBOUNCE_MS = 200;
const PHONE_IDENTIFIER_WITH_COUNTRY_CODE_AND_TEN_DIGITS_REGEX = /^\+([1-9]\d{0,2})(\d{10})$/;
const HEX_COLOR_OR_EMPTY_REGEX = /^$|^#([A-Fa-f0-9]{6})$/;
/** Matches `defaultConfig` in `src/assets/js/widget.js`. */
const WIDGET_CHAT_BUTTON_DEFAULTS = {
  chatButtonColor: '#002752',
  chatButtonTextColor: '#ffffff',
  chatButtonLabel: 'Schedule Service',
} as const;
const WILDCARD_DOMAIN = '*';
const INVALID_ALLOWED_DOMAIN_URL_ERROR = 'ERRORS.invalidAllowedDomainUrl';
const DUPLICATE_ALLOWED_DOMAIN_ERROR = 'ERRORS.duplicateAllowedDomain';
const SAVE_ALLOWED_DOMAINS_ERROR = 'ERRORS.allowedDomainsUpdateFailed';
const TAGGING_PROVIDER = 'AIVA';
const TAGGING_ENTITY_TYPE = 'BOT' as const;
const TAGGING_IDENTIFIER_VARIABLE = 'phoneNumber';
const LEGACY_TAGGING_IDENTIFIER_VARIABLE = 'CONTACT_PHONE';
const CLOSE_ACTION_KEY = 'BUTTON_ACTIONS.close';
const BOT_NAME_PLACEHOLDER = '{BOT_NAME}';
const DEFAULT_WELCOME_LANGUAGE = 'en-US';
const STEP_3_DEFAULT_LANGUAGE_CONTROL = 'step3.defaultLanguage';
/** Form control path for open-hours welcome text (matches existing `welcomeMessage` control name). */
const STEP_3_WELCOME_MESSAGE_CONTROL = 'step3.welcomeMessage';
const STEP_3_ENABLE_REPAIR_COST_ESTIMATE_CONTROL = 'step3.enableRepairCostEstimate';
const REPAIR_COST_ESTIMATE_INTENT_DISPLAY_NAME = 'Repair Cost Estimate (All Bots)';
/** AIVA-1768 — financing estimates ride on the same step3 group as Repair Cost Estimate. */
const STEP_3_ENABLE_FINANCING_ESTIMATE_CONTROL = 'step3.enableFinancingEstimate';
/** Form control path for off-hours welcome text (legacy control name uses lowercase "off"). */
const STEP_3_WELCOME_MESSAGE_OFF_HOURS_CONTROL = 'step3.welcomeMessageoffHours';
/**
 * AIVA-2044 — dialect codes this UI used to persist, mapped to the code the
 * platform actually accepts. `es-ES` was written by every save before this
 * change, but the voicebot only ever emits `es-MX`
 * (transcription-strategy.ts:33-39), so `es-ES` never matched and Spanish was
 * silently dropped. Stored bots and saved drafts still hold the old value, so
 * every read normalizes it — a bot is not migrated until it is saved again.
 */
const LEGACY_LANGUAGE_ALIASES: Readonly<Record<string, string>> = {
  'es-ES': 'es-MX',
};

/** Normalize one stored dialect code. Unknown/empty values pass through unchanged. */
function normalizeLanguageCode(code: string): string {
  return LEGACY_LANGUAGE_ALIASES[code] ?? code;
}

const ENABLE_MULTILANGUAGE_CONTROL = 'step4.enableMultilanguage';
const ALLOWED_LANGUAGES_CONTROL = 'step5.allowedLanguages';
const WELCOME_DEFAULT_CONTROL_KEYS = ['welcomeMessage', 'welcomeMessageoffHours'] as const;

const BOTTOM_RIGHT = 'bottom-right';
const BOTTOM_LEFT = 'bottom-left';
const TOP_RIGHT = 'top-right';
const TOP_LEFT = 'top-left';
const SYSTEM_DEFAULT = 'System Default';
const SEMIBOLD = 'semibold';
const PILL = 'pill';
const SCHEDULE_SERVICE = 'Schedule Service';

type WelcomeMessageControlKey = (typeof WELCOME_DEFAULT_CONTROL_KEYS)[number];
type BotTypeForWelcomeDefaults = 'VOICE' | 'TEXT' | 'SMS';

interface WelcomeTemplateSet {
  welcomeMessage: string;
  welcomeMessageoffHours: string;
}

interface SearchSelectState {
  searchControl: FormControl;
  sentinelValue: string;
  formPath: string;
  previousValue: string | null;
}

interface IdentifierTagRule {
  id: number;
  tag: string | null;
  currentIdentifier: string;
  identifiers: string[];
}

/** AIVA-2183: the pipelines that use the one "Caller can interrupt" setting. */
export function hasCallerInterruptSetting(voicePipeline?: string): boolean {
  return voicePipeline === 'REALTIME' || voicePipeline === 'FLUX';
}

@Component({
  selector: 'app-bot-setup',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslateModule,
    MatDividerModule,
    MatRadioModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatSlideToggleModule,
    RouterLink,
    ChatbotCustomizationComponent,
    VoiceCatalogComponent,
  ],
  templateUrl: './bot-setup.component.html',
  styleUrls: ['./bot-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BotSetupComponent implements OnInit {
  @ViewChild(MatAccordion) accordion!: MatAccordion;
  @ViewChild('openHoursAudio') openHoursAudioRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('offHoursAudio') offHoursAudioRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('pronunciationAudio') pronunciationAudioRef?: ElementRef<HTMLAudioElement>;
  @ViewChild('chatCustomizationFileInput') chatCustomizationFileInputRef?: ElementRef<HTMLInputElement>;
  showLanguageStep = true;
  botSetupForm!: FormGroup;
  accounts$!: Observable<Account[]>;
  filteredAccounts$!: Observable<Account[]>;
  private _subAccounts$ = new BehaviorSubject<SubAccount[]>([]);
  get subAccounts(): SubAccount[] {
    return this._subAccounts$.getValue();
  }
  set subAccounts(value: SubAccount[]) {
    this._subAccounts$.next(value);
  }
  filteredSubAccounts$!: Observable<SubAccount[]>;
  readonly accountSearch: SearchSelectState = {
    searchControl: new FormControl(''),
    sentinelValue: ACCOUNT_SEARCH_OPTION_VALUE,
    formPath: `${STEP_1_GROUP}.accounts`,
    previousValue: null,
  };
  readonly subAccountSearch: SearchSelectState = {
    searchControl: new FormControl(''),
    sentinelValue: SUB_ACCOUNT_SEARCH_OPTION_VALUE,
    formPath: SUB_ACCOUNT_CONTROL,
    previousValue: null,
  };
  isModal = false;
  isOnboardingView = false;
  editMode = false;
  botToEdit!: BotData;
  /** NORA_FEATURES gate: shows the Flow / Voice Pipeline inputs in step 3. */
  readonly noraFeatures = environment.noraFeatures ?? false;

  /** AIVA-2091: mileage collection modes, offered only for AIVA2 bots. */
  readonly mileageCollectionOptions: ReadonlyArray<{ value: MileageCollection; labelKey: string; hintKey: string }> = [
    { value: 'OFF', labelKey: 'BOTS.mileageOffLabel', hintKey: 'BOTS.mileageOffHint' },
    { value: 'NICE', labelKey: 'BOTS.mileageNiceLabel', hintKey: 'BOTS.mileageNiceHint' },
    { value: 'REQUIRED', labelKey: 'BOTS.mileageRequiredLabel', hintKey: 'BOTS.mileageRequiredHint' },
  ];
  botCreated = false;
  repairCostEstimateUpdating = false;
  financingEstimateUpdating = false;
  step = signal(0);
  placementOptions = [
    { id: BOTTOM_RIGHT, label: 'Bottom Right', dotClass: 'bottom-1.5 right-1.5' },
    { id: BOTTOM_LEFT, label: 'Bottom Left', dotClass: 'bottom-1.5 left-1.5' },
    { id: TOP_RIGHT, label: 'Top Right', dotClass: 'top-1.5 right-1.5' },
    { id: TOP_LEFT, label: 'Top Left', dotClass: 'top-1.5 left-1.5' },
  ];

  getChatButtonPreviewStyle(): Record<string, string> {
    const s8 = this.botSetupForm.get('step8');
    const bgColor = s8?.get('chatButtonColor')?.value || '#1a2a4a';
    const textColor = s8?.get('chatButtonTextColor')?.value || '#FFFFFF';
    const font = s8?.get('buttonFont')?.value || SYSTEM_DEFAULT;
    const fontFamily = font === SYSTEM_DEFAULT ? 'inherit' : `'${font}', sans-serif`;
    const weightMap: Record<string, string> = {
      light: '300',
      regular: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
      extrabold: '800',
    };
    const fontWeight = weightMap[s8?.get('fontWeight')?.value || SEMIBOLD] || '600';
    const styleVal = s8?.get('buttonStyle')?.value || PILL;
    const borderRadius = styleVal === PILL ? '9999px' : styleVal === 'rounded' ? '10px' : '4px';
    const sizeVal = s8?.get('chatButtonSize')?.value || 'medium';
    let width = '190px';
    let height = '48px';
    let fontSize = '14px';
    if (sizeVal === 'small') {
      width = '140px';
      height = '36px';
      fontSize = '12px';
    } else if (sizeVal === 'large') {
      width = '220px';
      height = '56px';
      fontSize = '16px';
    } else if (sizeVal === 'custom') {
      width = (s8?.get('chatButtonWidth')?.value || 190) + 'px';
      height = (s8?.get('chatButtonHeight')?.value || 48) + 'px';
    }
    return {
      'background-color': bgColor,
      'color': textColor,
      'font-family': fontFamily,
      'font-weight': fontWeight,
      'font-size': fontSize,
      'border-radius': borderRadius,
      'width': width,
      'height': height,
      'border': 'none',
      'box-shadow': '0 4px 14px 0 rgba(0,0,0,0.18)',
      'letter-spacing': '0.01em',
    };
  }

  getPlacementDotClass(pos: string): string {
    const found = this.placementOptions.find(o => o.id === pos);
    return found ? found.dotClass : '';
  }

  getPlacementLabel(pos: string): string {
    const found = this.placementOptions.find(o => o.id === pos);
    return found ? found.label : '';
  }

  setChatBubbleTextSize(sz: string): void {
    this.botSetupForm.get('step8.chatBubbleTextSize')?.setValue(sz);
    this.botSetupForm.get('step8.chatBubbleTextSize')?.markAsDirty();
    this.cd.markForCheck();
  }

  setChatButtonSize(sz: string): void {
    this.botSetupForm.get('step8.chatButtonSize')?.setValue(sz);
    this.botSetupForm.get('step8.chatButtonSize')?.markAsDirty();
    this.cd.markForCheck();
  }

  setChatButtonFontWeight(w: string): void {
    this.botSetupForm.get('step8.fontWeight')?.setValue(w);
    this.botSetupForm.get('step8.fontWeight')?.markAsDirty();
    this.cd.markForCheck();
  }

  setChatButtonStyle(st: string): void {
    this.botSetupForm.get('step8.buttonStyle')?.setValue(st);
    this.botSetupForm.get('step8.buttonStyle')?.markAsDirty();
    this.cd.markForCheck();
  }

  setPlacement(pos: string): void {
    this.botSetupForm.get('step8.buttonPlacement')?.setValue(pos);
    this.botSetupForm.get('step8.buttonPlacement')?.markAsDirty();
    this.cd.markForCheck();
  }
  chatWindowSectionOpen = true;
  chatLauncherSectionOpen = true;
  isPlayingOffHours = false;
  isPlayingOpenHours = false;
  /** Index of the pronunciation entry currently rendering a preview, or null. */
  previewingPronunciationIndex: number | null = null;
  playingPronunciationIndex: number | null = null;
  /** "entryIndex.variantIndex" of the language-override row currently rendering/playing, or null. */
  previewingVariantKey: string | null = null;
  playingVariantKey: string | null = null;
  /** Latest welcome-audio ↔ dictionary sync result, or null before the first check. */
  welcomeSync: WelcomeSyncResult | null = null;
  /** Lowercased dictionary displays already baked into an in-sync welcome clip. */
  private welcomeBakedWords = new Set<string>();
  /** Lowercased dictionary displays present in a stale clip (pending regen on save). */
  private welcomePendingWords = new Set<string>();
  /** OPEN_HOURS/OFF_HOURS -> dictionary-applied spoken text, for a dictionary-aware preview. */
  private welcomeSpokenByType = new Map<string, string>();
  private welcomeSyncTrigger$ = new Subject<void>();
  /**
   * Auto-translated welcome templates for a language not in
   * defaultWelcomeTemplatesByTypeAndLanguage (e.g. Hindi/Farsi/Cantonese on
   * AIVA2 bots) — keyed by `${botType}:${language}`, so a repeated language
   * switch doesn't re-hit the translate endpoint every time.
   */
  private translatedWelcomeTemplateCache = new Map<string, WelcomeTemplateSet>();
  private readonly defaultWelcomeTemplatesByTypeAndLanguage: Record<
    BotTypeForWelcomeDefaults,
    Record<string, WelcomeTemplateSet>
  > = {
    VOICE: {
      'en-US': {
        welcomeMessage: `Calls are recorded for quality. Hello! I'm ava from ${BOT_NAME_PLACEHOLDER}.`,
        // eslint-disable-next-line max-len
        welcomeMessageoffHours: `Calls are recorded for quality. Hello! I'm ava from ${BOT_NAME_PLACEHOLDER}. I can help you schedule an appointment even though our offices are closed.`,
      },
      'es-MX': {
        welcomeMessage: `Las llamadas son grabadas para control de calidad. ¡Hola! Soy ava de ${BOT_NAME_PLACEHOLDER}.`,
        // eslint-disable-next-line max-len
        welcomeMessageoffHours: `Las llamadas son grabadas para control de calidad. ¡Hola! Soy ava de ${BOT_NAME_PLACEHOLDER}. Puedo ayudarte a programar una cita aunque nuestras oficinas estén cerradas.`,
      },
      'fr-FR': {
        welcomeMessage: `Les appels sont enregistrés pour des raisons de qualité. Bonjour ! Je suis ava de ${BOT_NAME_PLACEHOLDER}.`,
        // eslint-disable-next-line max-len
        welcomeMessageoffHours: `Les appels sont enregistrés pour des raisons de qualité. Bonjour ! Je suis ava de ${BOT_NAME_PLACEHOLDER}. Je peux vous aider à prendre un rendez-vous même si nos bureaux sont fermés.`,
      },
      'pt-BR': {
        welcomeMessage: `As chamadas são gravadas para fins de qualidade. Olá! Sou ava da ${BOT_NAME_PLACEHOLDER}.`,
        // eslint-disable-next-line max-len
        welcomeMessageoffHours: `As chamadas são gravadas para fins de qualidade. Olá! Sou ava da ${BOT_NAME_PLACEHOLDER}. Posso ajudá-lo a agendar uma consulta mesmo que nossos escritórios estejam fechados.`,
      },
      'pl-PL': {
        welcomeMessage: `Rozmowy są nagrywane w celach jakościowych. Cześć! Jestem ava z ${BOT_NAME_PLACEHOLDER}.`,
        // eslint-disable-next-line max-len
        welcomeMessageoffHours: `Rozmowy są nagrywane w celach jakościowych. Cześć! Jestem ava z ${BOT_NAME_PLACEHOLDER}. Mogę pomóc Ci umówić wizytę, mimo że nasze biura są zamknięte.`,
      },
    },
    TEXT: {
      'en-US': {
        welcomeMessage: `Hello! I'm AIVA from ${BOT_NAME_PLACEHOLDER}.`,
        welcomeMessageoffHours: `Hello! I'm AIVA from ${BOT_NAME_PLACEHOLDER}.`,
      },
      'es-MX': {
        welcomeMessage: `¡Hola! Soy AIVA de ${BOT_NAME_PLACEHOLDER}.`,
        welcomeMessageoffHours: `¡Hola! Soy AIVA de ${BOT_NAME_PLACEHOLDER}.`,
      },
      'fr-FR': {
        welcomeMessage: `Bonjour ! Je suis AIVA de ${BOT_NAME_PLACEHOLDER}.`,
        welcomeMessageoffHours: `Bonjour ! Je suis AIVA de ${BOT_NAME_PLACEHOLDER}.`,
      },
      'pt-BR': {
        welcomeMessage: `Olá! Sou AIVA da ${BOT_NAME_PLACEHOLDER}.`,
        welcomeMessageoffHours: `Olá! Sou AIVA da ${BOT_NAME_PLACEHOLDER}.`,
      },
      'pl-PL': {
        welcomeMessage: `Cześć! Jestem AIVA z ${BOT_NAME_PLACEHOLDER}.`,
        welcomeMessageoffHours: `Cześć! Jestem AIVA z ${BOT_NAME_PLACEHOLDER}.`,
      },
    },
    SMS: {
      'en-US': {
        welcomeMessage: `Hello! I'm AIVA from ${BOT_NAME_PLACEHOLDER}.`,
        welcomeMessageoffHours: `Hello! I'm AIVA from ${BOT_NAME_PLACEHOLDER}.`,
      },
      'es-MX': {
        welcomeMessage: `¡Hola! Soy AIVA de ${BOT_NAME_PLACEHOLDER}.`,
        welcomeMessageoffHours: `¡Hola! Soy AIVA de ${BOT_NAME_PLACEHOLDER}.`,
      },
      'fr-FR': {
        welcomeMessage: `Bonjour ! Je suis AIVA de ${BOT_NAME_PLACEHOLDER}.`,
        welcomeMessageoffHours: `Bonjour ! Je suis AIVA de ${BOT_NAME_PLACEHOLDER}.`,
      },
      'pt-BR': {
        welcomeMessage: `Olá! Sou AIVA da ${BOT_NAME_PLACEHOLDER}.`,
        welcomeMessageoffHours: `Olá! Sou AIVA da ${BOT_NAME_PLACEHOLDER}.`,
      },
      'pl-PL': {
        welcomeMessage: `Cześć! Jestem AIVA z ${BOT_NAME_PLACEHOLDER}.`,
        welcomeMessageoffHours: `Cześć! Jestem AIVA z ${BOT_NAME_PLACEHOLDER}.`,
      },
    },
  };
  private readonly autoGeneratedWelcomeMessages: Record<WelcomeMessageControlKey, string> = {
    welcomeMessage: '',
    welcomeMessageoffHours: '',
  };
  private readonly userEditedWelcomeMessages: Record<WelcomeMessageControlKey, boolean> = {
    welcomeMessage: false,
    welcomeMessageoffHours: false,
  };
  private isApplyingWelcomeDefaults = false;
  private lastAppliedWelcomeLanguage: string | null = null;
  welcomeGenerationError = false;
  showSmsFollowUp = false;
  smsBots: BotData[] = [];
  chatbotCustomization: ChatbotCustomization | null = null;
  chatCustomizationPreviewLogo: string | ArrayBuffer | null = null;
  chatCustomizationLogoFileName: string | null = null;
  allowedDomainControl = new FormControl('', { nonNullable: true });
  allowedDomainValidationError = '';
  isSavingAllowedDomains = false;
  private pendingFollowUpId: string | null = null;
  private isLoadingEditData = false;
  voiceFlavorPresets: VoiceFlavorPreset[] = [];
  voiceAttributeLevels: VoiceAttributeLevel[] = ['LOW', 'MEDIUM', 'HIGH'];
  isPlayingVoiceFlavorPreview = false;
  readonly fluxVoiceOptions = FLUX_VOICE_OPTIONS;
  readonly realtimeVoiceOptions = REALTIME_VOICE_OPTIONS;
  /** Flux or Realtime voice whose preview is being fetched / is playing (one at a time). */
  voicePreviewLoading: FluxVoice | RealtimeVoice | null = null;
  voicePreviewPlaying: FluxVoice | RealtimeVoice | null = null;
  private voicePreviewAudio: HTMLAudioElement | null = null;
  origins: Origin[] = [
    { label: 'Voice', value: 'VOICE' },
    { label: 'Text', value: 'TEXT' },
    { label: 'SMS', value: 'SMS' },
  ];
  languageOptions = [
    { label: 'English United States', controlName: 'us', value: 'en-US' },
    { label: 'Spanish Mexico', controlName: 'es', value: 'es-MX' },
    { label: 'French France', controlName: 'fr', value: 'fr-FR' },
    { label: 'Portuguese Brazil', controlName: 'pt', value: 'pt-BR' },
    { label: 'Polish Poland', controlName: 'pl', value: 'pl-PL' },
    // AIVA-2150: experimental (ElevenLabs multilingual claims support, not yet
    // manually verified — see ai-engine's voice-catalog.ts). Restricted to
    // AIVA2-flow bots via visibleLanguageOptions below, not offered on AIVA1.
    { label: 'Hindi India', controlName: 'hi', value: 'hi-IN', aiva2Only: true },
    { label: 'Farsi Iran', controlName: 'fa', value: 'fa-IR', aiva2Only: true },
    { label: 'Chinese Cantonese', controlName: 'yue', value: 'yue-CN', aiva2Only: true },
    // AIVA2 chat widget (TEXT) bots only; ai-engine onboarding rejects it anywhere else.
    { label: 'Chinese Mandarin', controlName: 'zh', value: 'zh-CN', aiva2Only: true, chatOnly: true },
  ];
  generalTagOptions: string[] = [];
  selectedGeneralTags: string[] = [];
  identifierTagRules: IdentifierTagRule[] = [];
  private nextIdentifierTagRuleId = 1;
  private isLoadingTagDefinitions = false;
  private readonly LOCAL_STORAGE_KEY = 'botSetupDraft';

  private readonly _formBuilder = inject(UntypedFormBuilder);
  private readonly dialogRef = inject(MatDialogRef<BotSetupComponent>, { optional: true });
  private readonly data = inject<{ bot?: BotData; dialogConfig?: Record<string, unknown> }>(MAT_DIALOG_DATA, {
    optional: true,
  });
  private readonly _subAccountsService = inject(SubAccountsService);
  private readonly _accountsService = inject(AccountsService);
  private readonly _botCrudService = inject(BotCrudService);
  private readonly _authService = inject(AuthService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translateService = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly botSetupStateService = inject(BotSetupStateService);
  private readonly _voiceCatalogService = inject(VoiceCatalogService);

  constructor() {
    this.isModal = !!this.dialogRef;
    this.editMode = !!this.data?.bot;
    this.botToEdit = (this.data?.bot ?? null) as BotData;
    // Closing the modal must not leave a voice preview playing.
    this.destroyRef.onDestroy(() => this.stopVoicePreview());
  }

  ngOnInit(): void {
    this.isOnboardingView = this.route.snapshot.routeConfig?.path === 'onboarding';

    const tkn = this.route.snapshot.queryParamMap.get('tkn');
    if (tkn && !this.isModal) {
      this._authService.accessToken = tkn;

      this._authService.verify().subscribe({
        next: () => {
          this.initializeForm();
        },
        error: () => {
          this.showSnackbarMessage('Invalid or expired token');
        },
      });
    } else {
      this.initializeForm();
    }
  }

  initializeForm(): void {
    this.accounts$ = this._accountsService.getAccounts().pipe(shareReplay(1));
    this.filteredAccounts$ = this.buildSearchFilter$(this.accounts$, this.accountSearch.searchControl);
    this.filteredSubAccounts$ = this.buildSearchFilter$(this._subAccounts$, this.subAccountSearch.searchControl);
    this.botSetupForm = this.buildForm();
    this.loadVoiceFlavorPresets();
    this.setupFormListeners();
    if (!this.editMode) this.setupAutoSave();

    if (this.botSetupStateService.hasState()) {
      this.restoreFromState();
      return;
    }

    if (this.editMode && this.botToEdit) {
      this.accounts$.subscribe({
        next: () => {
          this.setupEditMode();
        },
        error: () => {
          this.showSnackbarMessage('Failed to load accounts.');
        },
      });
    } else {
      this.restoreFormFromStorage();
    }
  }

  onSearchSelectionChange(state: SearchSelectState, value: string | null): void {
    if (value === state.sentinelValue) {
      this.botSetupForm.get(state.formPath)?.setValue(state.previousValue, { emitEvent: false });
    }
  }

  onSearchPanelOpenedChange(state: SearchSelectState, opened: boolean, inputEl?: HTMLInputElement): void {
    if (opened) {
      state.previousValue = this.botSetupForm.get(state.formPath)?.value ?? null;
      state.searchControl.setValue('');
      if (inputEl) setTimeout(() => inputEl.focus(), 100);
    } else {
      state.searchControl.setValue('');
    }
  }

  private buildSearchFilter$<T extends { displayName?: string }>(
    source$: Observable<T[]>,
    searchControl: FormControl
  ): Observable<T[]> {
    const term$ = searchControl.valueChanges.pipe(
      startWith(''),
      map((v: string) => (v ?? '').toString().toLowerCase().trim()),
      debounceTime(SEARCH_DEBOUNCE_MS),
      distinctUntilChanged()
    );
    return combineLatest([source$, term$]).pipe(
      map(([items, term]) => this.filterByDisplayName(items ?? [], term)),
      shareReplay(1)
    );
  }

  private filterByDisplayName<T extends { displayName?: string }>(items: T[], term: string): T[] {
    if (!term) return items;
    return items.filter(item => (item.displayName ?? '').toLowerCase().includes(term));
  }

  private setupFormListeners(): void {
    this.setupAccountSubAccountListeners();
    this.handleLanguageStepVisibility();
    this.handleOriginAutogeneration();
    this.handleVoicemailControlsByBotType();
    this.handleSmsFollowUpVisibility();
    this.handleChatCustomizationStepVisibility();
    this.setupWelcomeMessageDefaults();
    this.setupChatCustomizationPlacementListeners();
    this.clearAiva2OnlyLanguagesOnFlowChange();
    this.clearChatOnlyLanguagesOnTypeChange();
    this.enforceFluxEnglishOnly();
    this.lockDefaultLanguageInAllowedList();
  }

  /** The allowed-languages checkbox of the bot's default language, while multi-language is on. */
  isLockedDefaultLanguage(value: string): boolean {
    return !!this.botSetupForm.get(ENABLE_MULTILANGUAGE_CONTROL)?.value && value === this.pronunciationDefaultLanguage;
  }

  /**
   * With multi-language on, the default language is always an allowed
   * language: the call starts in it, but speech recognition and the engine only
   * switch between ticked languages, so leaving it unticked meant the call
   * could never come back to it. Its checkbox is kept ticked here (and made
   * unclickable in the template) rather than disable()d — a disabled control is
   * left out of the form value the save reads, which would drop it from
   * allowedLanguages. Changing the default ticks the new one; the old default
   * stays ticked but becomes editable again.
   */
  private lockDefaultLanguageInAllowedList(): void {
    const ensureDefaultTicked = (): void => {
      if (!this.botSetupForm.get(ENABLE_MULTILANGUAGE_CONTROL)?.value) return;
      const option = this.languageOptions.find(opt => opt.value === this.pronunciationDefaultLanguage);
      const control = option && this.botSetupForm.get(`${ALLOWED_LANGUAGES_CONTROL}.${option.controlName}`);
      if (control && control.value !== true) control.setValue(true);
    };
    ensureDefaultTicked();
    this.botSetupForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(ensureDefaultTicked);
  }

  /**
   * Flux TTS voices are English-only, so FLUX is only valid for a bot that
   * speaks English alone. Rather than switching the pipeline behind the user's
   * back, a FLUX selection on a bot with a non-English language is marked
   * invalid (which blocks saving) until they pick another pipeline or drop the
   * other language. The error sits on the whole form, not on step3: step3
   * invalid would lock the Multilanguage panel (it opens only while step3 is
   * valid), leaving no way to turn multi-language back off. A form-level
   * validator re-runs on every change, so it follows the languages too.
   */
  private enforceFluxEnglishOnly(): void {
    this.botSetupForm.addValidators(() => (this.fluxEnglishOnlyError ? { fluxEnglishOnly: true } : null));
    this.botSetupForm.updateValueAndValidity({ emitEvent: false });
  }

  /** FLUX is selected but the bot also speaks a non-English language. */
  get fluxEnglishOnlyError(): boolean {
    return this.botSetupForm?.get('step3.voicePipeline')?.value === 'FLUX' && this.botSpeaksNonEnglish;
  }

  /**
   * visibleLanguageOptions only hides the AIVA2-only languages; a value picked
   * while the bot was AIVA2 would still be submitted after switching to AIVA1.
   * Keys off the flow value itself (not isAiva2Bot, which also needs the beta
   * flag) so loading a saved AIVA2 bot in another build never wipes it.
   */
  private clearAiva2OnlyLanguagesOnFlowChange(): void {
    this.botSetupForm
      .get('step3.flow')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((flow: string) => {
        if (flow === 'AIVA2') return;
        this.clearLanguages(this.languageOptions.filter(opt => opt.aiva2Only));
      });
  }

  /** Same as above for the chat-only languages when the bot type leaves TEXT. */
  private clearChatOnlyLanguagesOnTypeChange(): void {
    this.botSetupForm
      .get('step2.type')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type: string) => {
        if (type === 'TEXT') return;
        this.clearLanguages(this.languageOptions.filter(opt => opt.chatOnly));
      });
  }

  /** Resets the default language and unticks the allowed languages among `options`. */
  private clearLanguages(options: typeof this.languageOptions): void {
    const defaultLanguage = this.botSetupForm.get(STEP_3_DEFAULT_LANGUAGE_CONTROL);
    if (options.some(opt => opt.value === defaultLanguage?.value)) {
      defaultLanguage?.setValue(DEFAULT_WELCOME_LANGUAGE);
      this.onDefaultLanguageChange();
    }
    const allowed = this.botSetupForm.get(ALLOWED_LANGUAGES_CONTROL) as FormGroup | null;
    for (const opt of options) {
      const control = allowed?.get(opt.controlName);
      if (control?.value) control.setValue(false);
    }
  }

  private setupChatCustomizationPlacementListeners(): void {
    const step8 = this.botSetupForm.get(STEP_8_GROUP) as FormGroup;
    if (!step8) return;

    step8
      .get('buttonPlacement')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateMarginsFromOffsets());
    step8
      .get('horizontalOffset')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateMarginsFromOffsets());
    step8
      .get('verticalOffset')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateMarginsFromOffsets());
    step8
      .get('advancedMarginControl')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((adv) => {
        if (!adv) {
          this.updateMarginsFromOffsets();
        }
      });
  }

  updateMarginsFromOffsets(): void {
    const step8 = this.botSetupForm.get(STEP_8_GROUP);
    if (!step8) return;

    const advanced = step8.get('advancedMarginControl')?.value;
    if (advanced) return;

    const placement = step8.get('buttonPlacement')?.value || BOTTOM_RIGHT;
    const horiz = step8.get('horizontalOffset')?.value ?? 40;
    const vert = step8.get('verticalOffset')?.value ?? 20;

    let top = 'auto';
    let bottom = 'auto';
    let left = 'auto';
    let right = 'auto';

    if (placement === BOTTOM_RIGHT) {
      bottom = `${vert}px`;
      right = `${horiz}px`;
    } else if (placement === BOTTOM_LEFT) {
      bottom = `${vert}px`;
      left = `${horiz}px`;
    } else if (placement === TOP_RIGHT) {
      top = `${vert}px`;
      right = `${horiz}px`;
    } else if (placement === TOP_LEFT) {
      top = `${vert}px`;
      left = `${horiz}px`;
    }

    step8.get('marginTop')?.setValue(top, { emitEvent: false });
    step8.get('marginBottom')?.setValue(bottom, { emitEvent: false });
    step8.get('marginLeft')?.setValue(left, { emitEvent: false });
    step8.get('marginRight')?.setValue(right, { emitEvent: false });
  }

  private handleChatCustomizationStepVisibility(): void {
    const customizationStep = this.botSetupForm.get(STEP_8_GROUP);
    const typeControl = this.botSetupForm.get(STEP_2_TYPE_CONTROL);

    if (!customizationStep || !typeControl) {
      return;
    }

    const evaluate = (type: string): void => {
      const isTextEditMode = this.editMode && type === 'TEXT';

      if (isTextEditMode) {
        customizationStep.enable({ emitEvent: false });
      } else {
        customizationStep.disable({ emitEvent: false });
      }
    };

    evaluate(typeControl.value);
    typeControl.valueChanges.subscribe((type: string) => evaluate(type));
  }

  private setupWelcomeMessageDefaults(): void {
    WELCOME_DEFAULT_CONTROL_KEYS.forEach(controlKey => this.setupWelcomeMessageDirtyTracking(controlKey));

    const botNameControl = this.botSetupForm.get('step2.displayNameBot');
    const botTypeControl = this.botSetupForm.get(STEP_2_TYPE_CONTROL);

    if (!botNameControl || !botTypeControl) {
      return;
    }

    if (this.editMode) {
      return;
    }

    // Create mode: bot name / type changes also regenerate (respects user edits)
    combineLatest([
      botNameControl.valueChanges.pipe(startWith(botNameControl.value)),
      botTypeControl.valueChanges.pipe(startWith(botTypeControl.value)),
    ])
      .pipe(debounceTime(SEARCH_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyWelcomeDefaultsIfEligible());

    this.applyWelcomeDefaultsIfEligible();
  }

  private setupWelcomeMessageDirtyTracking(controlKey: WelcomeMessageControlKey): void {
    const control = this.getWelcomeMessageControl(controlKey);
    if (!control) {
      return;
    }

    control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: string) => {
      if (this.isApplyingWelcomeDefaults) {
        return;
      }

      const normalizedValue = (value ?? '').trim();
      const normalizedDefault = (this.autoGeneratedWelcomeMessages[controlKey] ?? '').trim();
      this.userEditedWelcomeMessages[controlKey] = normalizedValue.length > 0 && normalizedValue !== normalizedDefault;
    });
  }

  async resetWelcomeMessageToDefault(controlKey: WelcomeMessageControlKey): Promise<void> {
    const control = this.getWelcomeMessageControl(controlKey);
    if (!control) {
      return;
    }

    const resolvedDefaults = await this.buildResolvedWelcomeDefaults();
    if (!resolvedDefaults) {
      return;
    }

    this.setAutoGeneratedWelcomeMessages(resolvedDefaults);
    this.userEditedWelcomeMessages[controlKey] = false;
    this.withApplyingWelcomeDefaults(() => control.setValue(resolvedDefaults[controlKey]));
    this.cd.markForCheck();
  }

  private async applyWelcomeDefaultsIfEligible(): Promise<void> {
    if (this.editMode) {
      return;
    }

    const botType = this.getBotTypeForWelcomeDefaults();
    const botName = this.getBotNameForWelcomeDefaults();

    if (!botType || !botName) {
      return;
    }

    const language = this.getLanguageForWelcomeDefaults();

    // If language was already applied via onDefaultLanguageChange(), don't overwrite it
    if (this.lastAppliedWelcomeLanguage !== null && this.lastAppliedWelcomeLanguage !== language) {
      return;
    }

    const templates = await this.getWelcomeTemplatesByTypeAndLanguage(botType, language);
    if (!this.welcomeInputsStillCurrent(botType, botName, language)) return;
    this.applyResolvedWelcomeDefaults(templates, botName);
  }

  onDefaultLanguageChange(): void {
    this.applyWelcomeDefaultsOnLanguageChange();
  }

  private async applyWelcomeDefaultsOnLanguageChange(): Promise<void> {
    const rawLanguage = this.botSetupForm.get(STEP_3_DEFAULT_LANGUAGE_CONTROL)?.value;
    if (!rawLanguage) {
      return;
    }

    const botType = this.getBotTypeForWelcomeDefaults();
    const botName = this.getBotNameForWelcomeDefaults() || this.botToEdit?.displayName?.trim() || '';

    if (!botType || !botName) {
      this.welcomeGenerationError = true;
      this.cd.markForCheck();
      return;
    }

    try {
      WELCOME_DEFAULT_CONTROL_KEYS.forEach((key) => {
        this.userEditedWelcomeMessages[key] = false;
      });

      const language = this.getLanguageForWelcomeDefaults();
      this.lastAppliedWelcomeLanguage = language;
      const templates = await this.getWelcomeTemplatesByTypeAndLanguage(botType, language);
      if (!this.welcomeInputsStillCurrent(botType, botName, language)) return;
      this.applyResolvedWelcomeDefaults(templates, botName);
      this.welcomeGenerationError = false;
    } catch {
      this.welcomeGenerationError = true;
      this.cd.markForCheck();
    }
  }

  retryWelcomeGeneration(): void {
    this.welcomeGenerationError = false;
    this.applyWelcomeDefaultsOnLanguageChange();
  }

  private applyResolvedWelcomeDefaults(templates: WelcomeTemplateSet, botName: string): void {
    const resolvedWelcomeMessages = this.resolveWelcomeMessagesForBot(templates, botName);
    this.setAutoGeneratedWelcomeMessages(resolvedWelcomeMessages);

    this.withApplyingWelcomeDefaults(() => {
      WELCOME_DEFAULT_CONTROL_KEYS.forEach((controlKey) => {
        if (this.userEditedWelcomeMessages[controlKey]) {
          return;
        }

        this.getWelcomeMessageControl(controlKey)?.setValue(resolvedWelcomeMessages[controlKey]);
      });
    });

    this.cd.markForCheck();
  }

  private async buildResolvedWelcomeDefaults(): Promise<WelcomeTemplateSet | null> {
    const botType = this.getBotTypeForWelcomeDefaults();
    const botName = this.getBotNameForWelcomeDefaults() || this.botToEdit?.displayName?.trim() || '';

    if (!botType || !botName) {
      return null;
    }

    const language = this.getLanguageForWelcomeDefaults();
    const templates = await this.getWelcomeTemplatesByTypeAndLanguage(botType, language);
    if (!this.welcomeInputsStillCurrent(botType, botName, language)) return null;
    return this.resolveWelcomeMessagesForBot(templates, botName);
  }

  /**
   * A translated default can resolve after the user already picked another
   * language (or renamed/retyped the bot); applying it then would overwrite
   * the newer selection with an older one.
   */
  private welcomeInputsStillCurrent(botType: BotTypeForWelcomeDefaults, botName: string, language: string): boolean {
    const currentName = this.getBotNameForWelcomeDefaults() || this.botToEdit?.displayName?.trim() || '';
    return (
      this.getBotTypeForWelcomeDefaults() === botType &&
      currentName === botName &&
      this.getLanguageForWelcomeDefaults() === language
    );
  }

  private resolveWelcomeMessagesForBot(templates: WelcomeTemplateSet, botName: string): WelcomeTemplateSet {
    return {
      welcomeMessage: this.resolveBotNamePlaceholder(templates.welcomeMessage, botName),
      welcomeMessageoffHours: this.resolveBotNamePlaceholder(templates.welcomeMessageoffHours, botName),
    };
  }

  private setAutoGeneratedWelcomeMessages(messages: WelcomeTemplateSet): void {
    this.autoGeneratedWelcomeMessages.welcomeMessage = messages.welcomeMessage;
    this.autoGeneratedWelcomeMessages.welcomeMessageoffHours = messages.welcomeMessageoffHours;
  }

  private withApplyingWelcomeDefaults(work: () => void): void {
    this.isApplyingWelcomeDefaults = true;
    work();
    this.isApplyingWelcomeDefaults = false;
  }

  private getWelcomeMessageControl(controlKey: WelcomeMessageControlKey): AbstractControl | null {
    return this.botSetupForm.get(`step3.${controlKey}`);
  }

  private resolveBotNamePlaceholder(template: string, botName: string): string {
    return template.replaceAll(BOT_NAME_PLACEHOLDER, botName.trim());
  }

  private getBotTypeForWelcomeDefaults(): BotTypeForWelcomeDefaults | null {
    const botType = (this.botSetupForm.get(STEP_2_TYPE_CONTROL)?.value || '').toUpperCase();
    if (botType === 'VOICE' || botType === 'TEXT' || botType === 'SMS') {
      return botType;
    }
    return null;
  }

  private getBotNameForWelcomeDefaults(): string | null {
    const botName = (this.botSetupForm.get('step2.displayNameBot')?.value || '').trim();
    return botName || null;
  }

  private getLanguageForWelcomeDefaults(): string {
    return (this.botSetupForm.get(STEP_3_DEFAULT_LANGUAGE_CONTROL)?.value || DEFAULT_WELCOME_LANGUAGE) as string;
  }

  /**
   * Resolves the default welcome templates for botType/language. When there's
   * no hand-written entry for this language (e.g. Hindi/Farsi/Cantonese —
   * AIVA2-only languages that have no manually-reviewed template yet), the
   * English template is translated on the fly via ai-engine's
   * LanguageService (the same LLM-backed translator other runtime phrases
   * use), so the onboarding default isn't silently left in English. Falls
   * back to English if translation fails, rather than blocking onboarding.
   */
  private async getWelcomeTemplatesByTypeAndLanguage(
    botType: BotTypeForWelcomeDefaults,
    language: string
  ): Promise<WelcomeTemplateSet> {
    const hardcoded = this.defaultWelcomeTemplatesByTypeAndLanguage[botType][language];
    if (hardcoded) {
      return hardcoded;
    }

    const englishTemplates = this.defaultWelcomeTemplatesByTypeAndLanguage[botType][DEFAULT_WELCOME_LANGUAGE];
    const cacheKey = `${botType}:${language}`;
    const cached = this.translatedWelcomeTemplateCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const result = await firstValueFrom(
        this._botCrudService.translateWelcomeTemplates(
          [
            { id: 'welcomeMessage', text: englishTemplates.welcomeMessage },
            { id: 'welcomeMessageoffHours', text: englishTemplates.welcomeMessageoffHours },
          ],
          language
        )
      );

      const byId = new Map(result.messages.map(m => [m.id, m.translatedText]));
      const translated: WelcomeTemplateSet = {
        welcomeMessage: byId.get('welcomeMessage') ?? englishTemplates.welcomeMessage,
        welcomeMessageoffHours: byId.get('welcomeMessageoffHours') ?? englishTemplates.welcomeMessageoffHours,
      };
      this.translatedWelcomeTemplateCache.set(cacheKey, translated);
      return translated;
    } catch {
      return englishTemplates;
    }
  }

  /**
   * AIVA-2044 — a draft or saved state written before this change can still hold
   * `es-ES` in the two places the form stores a raw dialect code. (The allowed-
   * languages group is keyed by controlName with boolean values, so it carries no
   * dialect code and needs nothing here.) Mutates a plain parsed object, never
   * the live form.
   */
  private normalizeDraftLanguages(formValue: unknown): unknown {
    if (!formValue || typeof formValue !== 'object') return formValue;
    const draft = formValue as Record<string, Record<string, unknown>>;

    const step3 = draft['step3'];
    if (typeof step3?.['defaultLanguage'] === 'string') {
      step3['defaultLanguage'] = normalizeLanguageCode(step3['defaultLanguage']);
    }

    const overrides = draft['step6']?.['pronunciationOverrides'];
    if (Array.isArray(overrides)) {
      overrides.forEach((entry: { variants?: Array<Record<string, unknown>> }) => {
        if (!Array.isArray(entry?.variants)) return;
        entry.variants.forEach((variant) => {
          if (typeof variant?.['language'] === 'string') {
            variant['language'] = normalizeLanguageCode(variant['language']);
          }
        });
      });
    }

    return draft;
  }

  private restoreFromState(): void {
    const savedState = this.botSetupStateService.getState();
    this.botSetupForm.patchValue((this.normalizeDraftLanguages(savedState.formValue) ?? {}) as never);
    this.step.set(savedState.step);
    this.botSetupStateService.clearState();
  }

  private setupEditMode(): void {
    this.isLoadingEditData = true;
    const accountUID = this.botToEdit.accountUID!;

    this.setFormValue(`${STEP_1_GROUP}.accounts`, accountUID);
    this.loadSubAccountsForEdit(accountUID);
  }

  private loadSubAccountsForEdit(accountUID: string): void {
    this._subAccountsService.getSubAccountsByAccount(accountUID).subscribe({
      next: (subs) => {
        this.subAccounts = subs || [];
        this.cd.detectChanges();
        this.initializeEditModeData();
      },
      error: (error) => {
        console.error('Error loading sub-accounts:', error);
        this.showSnackbarMessage('Failed to load sub-accounts for the selected account.');
      },
    });
  }

  private initializeEditModeData(): void {
    const matchedSub = this.findSubAccount(this.botToEdit.subAccountUID!);

    if (matchedSub) {
      this.enableAndSetSubAccount(matchedSub);
    }

    this.patchFormWithBot(this.botToEdit);
    this.loadRepairCostEstimateStatus();
    this.loadFinancingEstimateStatus();

    if (this.botToEdit.type === 'VOICE' && this.botToEdit._id) {
      this.loadFollowUpConfiguration(this.botToEdit._id);
      // Flag whether the pre-rendered welcome audio is out of sync with the
      // pronunciation dictionary on open, and keep it fresh as the user edits
      // the dictionary or welcome text (debounced).
      this.checkWelcomeSync();
      this.watchWelcomeSync();
    }

    if (this.botToEdit._id) {
      this.loadTaggingRules(this.botToEdit._id);
    }

    if (this.botToEdit.type === 'TEXT') {
      this.loadChatbotCustomization(this.botToEdit._id!);
      this.setStep(-1);
    }

    this.isLoadingEditData = false;
  }

  private loadChatbotCustomization(botId: string): void {
    this._botCrudService.getChatbotCustomization(botId).subscribe({
      next: (config) => {
        this.chatbotCustomization = config || null;
        this.chatCustomizationPreviewLogo = config?.logoURL || null;
        this.patchChatCustomizationStep(config);
        this.botSetupForm.get(STEP_8_GROUP)?.markAsPristine();
        this.cd.detectChanges();
      },
      error: () => {
        this.chatbotCustomization = null;
        this.chatCustomizationPreviewLogo = null;
        this.patchChatCustomizationStep(null);
        this.cd.detectChanges();
      },
    });
  }

  onChatCustomizationStepOpened(): void {
    this.setStep(8);
    this.applyWidgetChatButtonDefaultsIfEmpty();
    this.cd.detectChanges();
  }

  private patchChatCustomizationStep(config: ChatbotCustomization | null | undefined): void {
    const customizationStep = this.botSetupForm.get(STEP_8_GROUP);
    if (!customizationStep) {
      return;
    }

    if (customizationStep.disabled && this.isTextBotEditSession) {
      customizationStep.enable({ emitEvent: false });
    }

    customizationStep.patchValue(this.mapCustomizationToStepValue(config));
    this.applyWidgetChatButtonDefaultsIfEmpty();
  }

  private applyWidgetChatButtonDefaultsIfEmpty(): void {
    const customizationStep = this.botSetupForm.get(STEP_8_GROUP);
    if (!customizationStep) {
      return;
    }

    (Object.keys(WIDGET_CHAT_BUTTON_DEFAULTS) as Array<keyof typeof WIDGET_CHAT_BUTTON_DEFAULTS>).forEach((key) => {
      const control = customizationStep.get(key);
      if (!control) {
        return;
      }
      const current = (control.value ?? '').toString().trim();
      if (!current) {
        control.setValue(WIDGET_CHAT_BUTTON_DEFAULTS[key], { emitEvent: true });
      }
    });
  }

  private _isSimplePlacement(
    placement: string,
    marginTop: string,
    marginBottom: string,
    marginLeft: string,
    marginRight: string
  ): boolean {
    if (placement === BOTTOM_RIGHT) {
      return marginTop === 'auto' && marginLeft === 'auto' && marginBottom !== 'auto' && marginRight !== 'auto';
    }
    if (placement === BOTTOM_LEFT) {
      return marginTop === 'auto' && marginRight === 'auto' && marginBottom !== 'auto' && marginLeft !== 'auto';
    }
    if (placement === TOP_RIGHT) {
      return marginBottom === 'auto' && marginLeft === 'auto' && marginTop !== 'auto' && marginRight !== 'auto';
    }
    if (placement === TOP_LEFT) {
      return marginBottom === 'auto' && marginRight === 'auto' && marginTop !== 'auto' && marginLeft !== 'auto';
    }
    return false;
  }

  private _parseOffsetsAndMargins(
    placement: string,
    marginTop: string,
    marginBottom: string,
    marginLeft: string,
    marginRight: string
  ): { advancedMarginControl: boolean; horizontalOffset: number; verticalOffset: number } {
    const parsePx = (val: string): number => {
      const match = val.match(/^(\d+)px$/);
      return match ? parseInt(match[1], 10) : 0;
    };

    const isSimple = this._isSimplePlacement(placement, marginTop, marginBottom, marginLeft, marginRight);
    if (!isSimple) {
      return { advancedMarginControl: true, horizontalOffset: 40, verticalOffset: 20 };
    }

    let verticalOffset = 0;
    let horizontalOffset = 0;

    if (placement === BOTTOM_RIGHT) {
      verticalOffset = parsePx(marginBottom);
      horizontalOffset = parsePx(marginRight);
    } else if (placement === BOTTOM_LEFT) {
      verticalOffset = parsePx(marginBottom);
      horizontalOffset = parsePx(marginLeft);
    } else if (placement === TOP_RIGHT) {
      verticalOffset = parsePx(marginTop);
      horizontalOffset = parsePx(marginRight);
    } else if (placement === TOP_LEFT) {
      verticalOffset = parsePx(marginTop);
      horizontalOffset = parsePx(marginLeft);
    }

    return { advancedMarginControl: false, horizontalOffset, verticalOffset };
  }

  private _resolveDirectMap(
    config: ChatbotCustomization | null | undefined,
    directMap: Record<string, { keys: (keyof ChatbotCustomization)[]; fallback: unknown }>
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [targetKey, mapInfo] of Object.entries(directMap)) {
      let val: unknown = null;
      if (config) {
        for (const k of mapInfo.keys) {
          if (config[k] !== undefined && config[k] !== null && config[k] !== '') {
            val = config[k];
            break;
          }
        }
      }
      result[targetKey] = val || mapInfo.fallback;
    }
    return result;
  }

  private _resolveCssMap(
    config: ChatbotCustomization | null | undefined,
    cssObj: Record<string, string>,
    cssMap: Record<string, { configKey?: keyof ChatbotCustomization; cssKey: string; fallback: unknown }>
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [targetKey, mapInfo] of Object.entries(cssMap)) {
      let val: unknown = null;
      if (
        config &&
        mapInfo.configKey &&
        config[mapInfo.configKey] !== undefined &&
        config[mapInfo.configKey] !== null &&
        config[mapInfo.configKey] !== ''
      ) {
        val = config[mapInfo.configKey];
      } else if (cssObj[mapInfo.cssKey] !== undefined && cssObj[mapInfo.cssKey] !== null && cssObj[mapInfo.cssKey] !== '') {
        val = cssObj[mapInfo.cssKey];
      }
      result[targetKey] = val || mapInfo.fallback;
    }
    return result;
  }

  private _getDimension(configVal: number | string | undefined, cssVal: string | undefined, fallback: number): number {
    if (configVal !== undefined && configVal !== null && configVal !== '') {
      return Number(configVal);
    }
    if (cssVal !== undefined && cssVal !== null && cssVal !== '') {
      return Number(cssVal);
    }
    return fallback;
  }

  private mapCustomizationToStepValue(config: ChatbotCustomization | null | undefined): Record<string, unknown> {
    const placement = config?.buttonPlacement || config?.placement || BOTTOM_RIGHT;
    const marginTop = config?.marginTop || 'auto';
    const marginBottom = config?.marginBottom || (placement.startsWith('bottom') ? '20px' : 'auto');
    const marginLeft = config?.marginLeft || 'auto';
    const marginRight = config?.marginRight || (placement.endsWith('right') ? '40px' : 'auto');
    const chatButtonZIndex =
      config?.chatButtonZIndex !== undefined
        ? config.chatButtonZIndex
        : config?.zIndexButton !== undefined
          ? config.zIndexButton
          : 1000;
    const chatWindowZIndex =
      config?.chatWindowZIndex !== undefined
        ? config.chatWindowZIndex
        : config?.zIndexChatWindow !== undefined
          ? config.zIndexChatWindow
          : 1000;

    const { advancedMarginControl, horizontalOffset, verticalOffset } = this._parseOffsetsAndMargins(
      placement,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight
    );

    let cssObj: Record<string, string> = {};
    if (config?.css) {
      try {
        cssObj = JSON.parse(config.css) as Record<string, string>;
      } catch {
        cssObj = {};
      }
    }

    const directMap: Record<string, { keys: (keyof ChatbotCustomization)[]; fallback: unknown }> = {
      headerBackgroundColor: { keys: ['headerBackgroundColor', 'headerColor'], fallback: '#EEEEEE' },
      botMessageBubbleColor: { keys: ['botMessageBubbleColor', 'botMessageColor'], fallback: '#f12424' },
      customerMessageBubbleColor: { keys: ['customerMessageBubbleColor', 'userMessageColor'], fallback: '#8069DB' },
      chatButtonColor: { keys: ['chatButtonColor', 'buttonsColor'], fallback: '#1a2a4a' },
    };

    const cssMap: Record<string, { configKey?: keyof ChatbotCustomization; cssKey: string; fallback: unknown }> = {
      headerTextColor: { cssKey: 'headerTextColor', fallback: '#FFFFFF' },
      headerFont: { configKey: 'headerFont', cssKey: 'headerFont', fallback: SYSTEM_DEFAULT },
      chatBubbleBackgroundColor: { cssKey: 'chatBubbleBackgroundColor', fallback: '#607785' },
      chatBubbleTextColor: { cssKey: 'chatBubbleTextColor', fallback: '#EEEEEE' },
      chatBubbleFont: { configKey: 'chatBubbleFont', cssKey: 'chatBubbleFont', fallback: SYSTEM_DEFAULT },
      chatBubbleTextSize: { cssKey: 'chatBubbleTextSize', fallback: 'medium' },
      chatButtonTextColor: { cssKey: 'chatButtonTextColor', fallback: '#FFFFFF' },
      chatButtonLabel: { cssKey: 'chatButtonLabel', fallback: SCHEDULE_SERVICE },
      chatButtonSize: { configKey: 'chatButtonSize', cssKey: 'chatButtonSize', fallback: 'medium' },
      buttonFont: { configKey: 'buttonFont', cssKey: 'chatButtonFont', fallback: SYSTEM_DEFAULT },
      fontWeight: { configKey: 'fontWeight', cssKey: 'chatButtonFontWeight', fallback: SEMIBOLD },
      buttonStyle: { configKey: 'buttonStyle', cssKey: 'chatButtonStyle', fallback: PILL },
    };

    const resolvedDirect = this._resolveDirectMap(config, directMap);
    const resolvedCss = this._resolveCssMap(config, cssObj, cssMap);

    return {
      ...resolvedDirect,
      ...resolvedCss,
      buttonPlacement: placement,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      chatButtonZIndex,
      chatWindowZIndex,
      advancedMarginControl,
      horizontalOffset,
      verticalOffset,
      headerLogo: config?.headerLogo || config?.logoURL || null,
      chatButtonWidth: this._getDimension(config?.chatButtonWidth, cssObj['chatButtonWidth'], 190),
      chatButtonHeight: this._getDimension(config?.chatButtonHeight, cssObj['chatButtonHeight'], 48),
    };
  }

  onChatCustomizationColorInput(event: Event, controlName: string): void {
    const value = (event.target as HTMLInputElement).value;
    const control = this.botSetupForm.get(`step8.${controlName}`);
    if (!control || control.value === value) {
      return;
    }

    control.setValue(value);
    control.markAsDirty();
    this.cd.markForCheck();
  }

  onChatCustomizationLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension === 'svg') {
      const reader = new FileReader();
      reader.onload = (fileEvent): void => {
        this.chatCustomizationPreviewLogo = fileEvent.target?.result ?? null;
        this.chatCustomizationLogoFileName = file.name;
        const logoControl = this.botSetupForm.get(`${STEP_8_GROUP}.headerLogo`);
        logoControl?.setValue(this.chatCustomizationPreviewLogo);
        logoControl?.markAsDirty();
        this.cd.markForCheck();
      };
      reader.readAsDataURL(file);
      return;
    }

    const image = new Image();
    const reader = new FileReader();
    reader.onload = (fileEvent): void => {
      image.src = (fileEvent.target?.result as string) || '';
      image.onload = (): void => {
        if (image.width === 500 && image.height === 500) {
          this.chatCustomizationPreviewLogo = image.src;
          this.chatCustomizationLogoFileName = file.name;
          const logoControl = this.botSetupForm.get(`${STEP_8_GROUP}.headerLogo`);
          logoControl?.setValue(this.chatCustomizationPreviewLogo);
          logoControl?.markAsDirty();
          this.cd.markForCheck();
          return;
        }

        this.showSnackbarMessage(
          this.translateService.instant('BOT_CUSTOMIZATION.image500x500px', {
            width: image.width,
            height: image.height,
          })
        );
        this.removeChatCustomizationLogo();
      };
    };
    reader.readAsDataURL(file);
  }

  removeChatCustomizationLogo(event?: Event): void {
    event?.stopPropagation();
    this.chatCustomizationPreviewLogo = null;
    this.chatCustomizationLogoFileName = null;
    const logoControl = this.botSetupForm.get(`${STEP_8_GROUP}.headerLogo`);
    logoControl?.setValue(null);
    logoControl?.markAsDirty();
    if (this.chatCustomizationFileInputRef?.nativeElement) {
      this.chatCustomizationFileInputRef.nativeElement.value = '';
    }
    this.cd.markForCheck();
  }

  /**
   * Resets all Chatbot Customization (step 8) fields back to their default
   * values and marks the step pristine.  Also clears any parent-side logo
   * state.  Wired to the Reset button in the expansion-panel header and called
   * automatically when the dialog is closed without saving.
   */
  resetChatbotCustomizationStep(): void {
    const step8 = this.botSetupForm.get(STEP_8_GROUP);
    if (!step8) return;

    step8.patchValue({
      headerBackgroundColor: '#EEEEEE',
      headerTextColor: '#FFFFFF',
      headerFont: SYSTEM_DEFAULT,
      botMessageBubbleColor: '#f12424',
      customerMessageBubbleColor: '#8069DB',
      chatBubbleBackgroundColor: '#607785',
      chatBubbleTextColor: '#EEEEEE',
      chatBubbleFont: SYSTEM_DEFAULT,
      chatBubbleTextSize: 'medium',
      chatButtonColor: '#1a2a4a',
      chatButtonTextColor: '#FFFFFF',
      chatButtonLabel: SCHEDULE_SERVICE,
      chatButtonSize: 'medium',
      chatButtonWidth: 190,
      chatButtonHeight: 48,
      buttonFont: SYSTEM_DEFAULT,
      fontWeight: SEMIBOLD,
      buttonStyle: PILL,
      headerLogo: null,
      buttonPlacement: BOTTOM_RIGHT,
      marginTop: 'auto',
      marginBottom: '20px',
      marginLeft: 'auto',
      marginRight: '40px',
      chatButtonZIndex: 1000,
      chatWindowZIndex: 1000,
      advancedMarginControl: false,
      horizontalOffset: 40,
      verticalOffset: 20,
    });
    step8.markAsPristine();

    // Clear legacy parent-side logo state so the old inline handler
    // (removeChatCustomizationLogo) stays consistent if ever called.
    this.chatCustomizationPreviewLogo = null;
    this.chatCustomizationLogoFileName = null;
    this.cd.markForCheck();
  }

  private loadFollowUpConfiguration(botId: string): void {
    this._botCrudService.getFollowUpConfiguration(botId).subscribe({
      next: (configs) => {
        if (configs?.length > 0) {
          this.showSmsFollowUp = true;
          this.botSetupForm.get('step7.enableSmsFollowUp')?.setValue(true);

          const followUpId = this.resolveFollowUpConfiguration(configs)?.followUpId;
          if (followUpId) {
            this.setLinkedSmsBotByFollowUpId(followUpId);
          }

          this.cd.detectChanges();
        }
      },
      error: () => {
        this.showSmsFollowUp = false;
      },
    });
  }

  /**
   * `identifier` is not unique, so this endpoint can return several configurations. Taking
   * `configs[0]` could show a link belonging to another dealership, so prefer the one scoped
   * to the dealer being edited.
   */
  private resolveFollowUpConfiguration(configs: FollowUpConfiguration[]): FollowUpConfiguration | undefined {
    if (configs.length > 1) {
      console.warn(
        `Bot has ${configs.length} follow-up configurations for one identifier; expected one.`,
        configs.map(config => config._id)
      );
    }

    const subAccountUID = this.botSetupForm.get(SUB_ACCOUNT_CONTROL)?.value;
    return (
      configs.find(config => config.subAccountUID === subAccountUID) ??
      // Older configurations have no dealer stamped on them.
      configs.find(config => !config.subAccountUID) ??
      configs[0]
    );
  }

  private setLinkedSmsBotByFollowUpId(followUpId: string): void {
    const matchedBot = this.smsBots.find(bot => bot._id === followUpId);

    if (matchedBot) {
      this.botSetupForm.get('step7.linkedVoiceBot')?.setValue(matchedBot._id);
      this.pendingFollowUpId = null;
    } else {
      this.pendingFollowUpId = followUpId;
    }
  }

  private enableAndSetSubAccount(subAccount: SubAccount): void {
    const subAccountControl = this.botSetupForm.get(SUB_ACCOUNT_CONTROL);
    subAccountControl?.enable();
    this.setFormValue(SUB_ACCOUNT_CONTROL, subAccount.subAccountUID);

    this.loadSmsBotsForCurrentContext(subAccount);
  }

  private setFormValue(path: string, value: unknown): void {
    this.botSetupForm.get(path)?.setValue(value, { emitEvent: false });
  }

  private findSubAccount(subAccountUID: string): SubAccount | undefined {
    return (
      this.subAccounts.find(s => s._id === subAccountUID) ||
      this.subAccounts.find(s => s.subAccountUID === subAccountUID)
    );
  }

  getWelcomeMessage(bot: BotData, type: 'OPEN_HOURS' | 'OFF_HOURS' = 'OPEN_HOURS'): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const welcomeMessages = (bot.settings as any)?.welcomeMessages || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messageObj = welcomeMessages.find((wm: any) => wm.type === type);
    if (messageObj) {
      return messageObj.plainText || messageObj.message || '';
    }

    if (type === 'OPEN_HOURS') {
      return bot.settings?.welcomeMessage || '';
    }
    return '';
  }

  private patchFormWithBot(bot: BotData): void {
    const selectedSub = this.findSubAccount(bot.subAccountUID);
    const systemKeyMap = this.buildSystemKeyMap(selectedSub);

    this.botSetupForm.patchValue({
      step2: this.getStep2Values(bot),
      step3: this.getStep3Values(bot),
      step4: this.getStep4Values(bot),
      step6: this.getStep6Values(bot),
      step9: this.getStep9Values(bot, systemKeyMap),
    });

    this.patchAllowedLanguages(bot);
    this.setDtmfMenuOptionsFromBot(bot);
    this.setPronunciationOverridesFromBot(bot);
    this.syncVoicemailControlsForBotType();
    this.cd.detectChanges();
  }

  private getStep2Values(bot: BotData): {
    displayNameBot: string;
    type: string;
    origin: string;
  } {
    return {
      displayNameBot: bot.displayName,
      type: bot.type,
      origin: bot.service?.origin || '',
    };
  }

  private getStep3Values(bot: BotData): {
    defaultLanguage: string;
    welcomeMessage: string;
    welcomeMessageoffHours: string;
    voiceFlavor: string;
    voiceTone: VoiceAttributeLevel;
    voiceFriendliness: VoiceAttributeLevel;
    voiceProfessionalism: VoiceAttributeLevel;
    enableRepairCostEstimate: boolean;
    enableFinancingEstimate: boolean;
    flow: 'AIVA1' | 'AIVA2';
    voicePipeline: 'DEFAULT' | 'REALTIME' | 'FLUX';
    fluxVoice: FluxVoice;
    realtimeVoice: RealtimeVoice;
    callerCanInterrupt: boolean;
    mileageCollection: MileageCollection;
  } {
    const voice = bot.settings?.voice;
    const hasRepairCostEstimate = (bot.intents || []).some(
      intent => intent.displayName === REPAIR_COST_ESTIMATE_INTENT_DISPLAY_NAME
    );
    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      defaultLanguage: normalizeLanguageCode((bot.settings as any)?.languageCode || ''),
      welcomeMessage: this.getWelcomeMessage(bot),
      welcomeMessageoffHours: this.getWelcomeMessage(bot, 'OFF_HOURS'),
      voiceFlavor: voice?.voiceFlavor || 'WARM_FRIENDLY',
      voiceTone: voiceAttributeLevelFromValue(voice?.voiceAttributes?.tone),
      voiceFriendliness: voiceAttributeLevelFromValue(voice?.voiceAttributes?.friendliness),
      voiceProfessionalism: voiceAttributeLevelFromValue(voice?.voiceAttributes?.professionalism),
      enableRepairCostEstimate: hasRepairCostEstimate,
      enableFinancingEstimate: bot.financingEstimateEnabled === true,
      flow: bot.settings?.flow ?? 'AIVA1',
      voicePipeline: bot.settings?.voicePipeline ?? 'DEFAULT',
      fluxVoice: this.toFluxVoice(bot.settings?.fluxVoice),
      realtimeVoice: this.toRealtimeVoice(bot.settings?.realtimeVoice),
      // One value for both pipelines; unset shows off.
      callerCanInterrupt: bot.settings?.callerCanInterrupt === true,
      mileageCollection: this.toMileageCollection(bot.settings?.mileageCollection),
    };
  }

  /** A saved value we don't recognise (or none) loads as OFF, the engine's default. */
  private toMileageCollection(value: string | undefined): MileageCollection {
    return this.mileageCollectionOptions.find(option => option.value === value)?.value ?? 'OFF';
  }

  private getStep4Values(bot: BotData): {
    enableMultilanguage: boolean;
  } {
    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      enableMultilanguage: !!(bot.settings as any)?.allowedLanguages?.length,
    };
  }

  private getStep6Values(bot: BotData): {
    callForwardingPhoneNo: string;
    callForwardingExtension: string;
    afterHoursForwardingPhrase: string;
    allowTransferWithoutCustomer: boolean;
    enableVoicemail: boolean;
    voicemailConsentMessage: string;
    voicemailGreeting: string;
    voicemailMaxDurationSeconds: number;
    enableDtmf: boolean;
    dtmfMenuPrompt: string;
    dtmfMaxRetries: number;
    dtmfInputTimeoutMs: number;
    dtmfFallbackPrompt: string;
  } {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const voice = bot.settings?.voice as any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const transfer = bot.settings?.nlp?.contextualFunctions?.forwardToLiveAgent as any;

    return {
      callForwardingPhoneNo: transfer?.phoneNumber || '',
      callForwardingExtension: transfer?.extension || '',
      afterHoursForwardingPhrase: transfer?.afterHoursForwardingPhrase || '',
      allowTransferWithoutCustomer: transfer?.allowTransferWithoutCustomer ?? false,

      enableVoicemail: voice?.enableVoicemail ?? false,
      voicemailConsentMessage: voice?.voicemailConsentMessage || '',
      voicemailGreeting: voice?.voicemailGreeting || '',
      voicemailMaxDurationSeconds: voice?.voicemailMaxDurationSeconds ?? 120,

      enableDtmf: voice?.dtmf?.enabled ?? !!voice?.dtmf?.options?.length,
      dtmfMenuPrompt: voice?.dtmf?.menuPrompt || 'Please make a selection from your keypad.',
      dtmfMaxRetries: voice?.dtmf?.maxRetries ?? 2,
      dtmfInputTimeoutMs: voice?.dtmf?.inputTimeoutMs ?? 8000,
      dtmfFallbackPrompt:
        voice?.dtmf?.fallbackAction?.prompt || 'We did not receive a keypad entry. Please stay on the line for assistance.',
    };
  }

  private getStep9Values(
    bot: BotData,
    systemKeyMap: ReturnType<typeof this.buildSystemKeyMap>
  ): {
    fallbackTeamId: string;
    forceDefaultTeamOnly: boolean;
  } {
    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fallbackTeamId: (bot.settings as any)?.fallbackTeamId || systemKeyMap?.fallbackTeamId || '',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      forceDefaultTeamOnly: !!(bot.settings as any)?.forceDefaultTeamOnly,
    };
  }

  private patchAllowedLanguages(bot: BotData): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const langs: string[] = ((bot.settings as any)?.allowedLanguages || []).map(normalizeLanguageCode);

    const controls = this.botSetupForm.get(ALLOWED_LANGUAGES_CONTROL) as FormGroup;

    controls.patchValue(
      this.languageOptions.reduce(
        (acc, lang) => ({
          ...acc,
          [lang.controlName]: langs.includes(lang.value),
        }),
        {}
      )
    );
  }

  get dtmfMenuOptions(): UntypedFormArray {
    return this.botSetupForm.get('step6.dtmfMenuOptions') as UntypedFormArray;
  }

  addDtmfMenuOption(option?: {
    digit?: string;
    label?: string;
    actionType?: string;
    phoneNumber?: string;
    extension?: string;
    prompt?: string;
    flowBranchId?: string;
  }): void {
    this.dtmfMenuOptions.push(
      this._formBuilder.group({
        digit: [option?.digit ?? '', [Validators.required, Validators.pattern(/^[0-9*#]$/)]],
        label: [option?.label ?? ''],
        actionType: [option?.actionType ?? 'TRANSFER', Validators.required],
        phoneNumber: [option?.phoneNumber ?? '', [Validators.pattern(/^\+?[1-9]\d{9,14}(?:[xX#]\d+)?$/)]],
        extension: [option?.extension ?? '', [Validators.pattern(/^\d*$/)]],
        flowBranchId: [option?.flowBranchId ?? ''],
        prompt: [option?.prompt ?? ''],
      })
    );
  }

  removeDtmfMenuOption(index: number): void {
    this.dtmfMenuOptions.removeAt(index);
  }

  private setDtmfMenuOptionsFromBot(bot: BotData): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const options = ((bot.settings?.voice as any)?.dtmf?.options ?? []) as Array<{
      digit: string;
      label?: string;
      action?: {
        type?: string;
        phoneNumber?: string;
        extension?: string;
        prompt?: string;
        flowBranchId?: string;
      };
    }>;

    while (this.dtmfMenuOptions.length) {
      this.dtmfMenuOptions.removeAt(0);
    }

    if (!options.length) {
      this.addDtmfMenuOption({ digit: '1', actionType: 'TRANSFER', label: 'Sales' });
      this.addDtmfMenuOption({ digit: '2', actionType: 'REPEAT_MENU', label: 'Repeat menu' });
      return;
    }

    options.forEach(option =>
      this.addDtmfMenuOption({
        digit: option.digit,
        label: option.label,
        actionType: option.action?.type,
        phoneNumber: option.action?.phoneNumber,
        extension: option.action?.extension,
        prompt: option.action?.prompt,
        flowBranchId: option.action?.flowBranchId,
      })
    );
  }

  // ── Pronunciation dictionary (voice-only) ──────────────────────────────────
  // Each word has one default say-it form (applied to every language), plus an
  // optional list of per-language overrides for multi-language bots (AIVA-1924).

  /** Per-entry expand state for the language-specific overrides section. */
  private readonly pronunciationExpanded = new Set<AbstractControl>();

  get pronunciationEntries(): UntypedFormArray {
    return this.botSetupForm.get('step6.pronunciationOverrides') as UntypedFormArray;
  }

  isPronunciationExpanded(entry: AbstractControl): boolean {
    return this.pronunciationExpanded.has(entry);
  }

  togglePronunciationExpanded(entry: AbstractControl): void {
    if (this.pronunciationExpanded.has(entry)) {
      this.pronunciationExpanded.delete(entry);
    } else {
      this.pronunciationExpanded.add(entry);
    }
  }

  /** The bot's default spoken language code (e.g. 'en-US'); falls back to en-US. */
  get pronunciationDefaultLanguage(): string {
    return this.botSetupForm.get('step3.defaultLanguage')?.value || 'en-US';
  }

  /** Human label for the default language, for the primary "Say it as" field. */
  get pronunciationDefaultLanguageLabel(): string {
    const match = this.languageOptions.find(opt => opt.value === this.pronunciationDefaultLanguage);
    return match?.label ?? 'Default';
  }

  /** Languages the bot speaks: selected allowed languages (multi) or just the default. */
  get pronunciationBotLanguages(): { value: string; label: string }[] {
    const multiEnabled = !!this.botSetupForm.get(ENABLE_MULTILANGUAGE_CONTROL)?.value;
    if (multiEnabled) {
      const selected = this.botSetupForm.get(ALLOWED_LANGUAGES_CONTROL)?.value ?? {};
      const langs = this.languageOptions
        .filter(opt => selected[opt.controlName])
        .map(opt => ({ value: opt.value, label: opt.label }));
      if (langs.length) return langs;
    }
    const match = this.languageOptions.find(opt => opt.value === this.pronunciationDefaultLanguage);
    return match ? [{ value: match.value, label: match.label }] : [];
  }

  /** Language codes the bot speaks, for the voice-audition panel's language picker. */
  get voiceCatalogLanguages(): string[] {
    return this.pronunciationBotLanguages.map(l => l.value);
  }

  /** True when this bot can pick from the AIVA2-exclusive experimental languages. */
  get isAiva2Bot(): boolean {
    return this.noraFeatures && this.botSetupForm.get('step3.flow')?.value === 'AIVA2';
  }

  /** True when the bot speaks at least one non-English language. */
  get botSpeaksNonEnglish(): boolean {
    return this.voiceCatalogLanguages.some(lang => !lang.toLowerCase().startsWith('en'));
  }

  /** FLUX is offered only to a bot that speaks English alone (Flux TTS is English-only). */
  get fluxAvailable(): boolean {
    return !this.botSpeaksNonEnglish;
  }

  /**
   * Whether the bot's own voice (Voice Audition for AIVA2, Voice Flavor for
   * AIVA1) is ever spoken on this pipeline, mirroring aiva-voicebot:
   *  - REALTIME: gpt-realtime speaks every line, greeting included, in its own
   *    voice; the bot's voices are never used.
   *  - FLUX: every turn is English (enforceFluxEnglishOnly) and spoken in the
   *    Flux voice picked under the pipeline, so the bot's voices are not used.
   *  - DEFAULT: the bot's voices speak every turn.
   */
  get botVoiceIsUsed(): boolean {
    const pipeline = this.botSetupForm.get('step3.voicePipeline')?.value;
    return pipeline !== 'REALTIME' && pipeline !== 'FLUX';
  }

  /**
   * Languages offered in Voice Audition. The same array is returned until the
   * list actually changes — a new array on every change-detection pass would
   * fire the panel's ngOnChanges each time and reset every voice's language
   * dropdown to its first language.
   */
  get auditionLanguages(): string[] {
    // Default language first: each voice's language dropdown pre-selects its
    // first compatible entry, and the default language's voice is the one that
    // records the greeting and speaks until the caller switches.
    const defaultLanguage = this.pronunciationDefaultLanguage;
    const all = this.voiceCatalogLanguages;
    const langs = all.includes(defaultLanguage) ? [defaultLanguage, ...all.filter(lang => lang !== defaultLanguage)] : all;
    const key = langs.join(',');
    if (key !== this.auditionLanguagesCache.key) {
      this.auditionLanguagesCache = { key, value: langs };
    }
    return this.auditionLanguagesCache.value;
  }
  private auditionLanguagesCache: { key: string; value: string[] } = { key: '', value: [] };

  /**
   * The Flux STT model a FLUX-pipeline bot will run on, mirroring aiva-voicebot's
   * flux-pipeline.ts: multilingual when multilanguage is enabled or the bot's
   * default language is not English, English otherwise.
   */
  get fluxSttModel(): string {
    const multi = this.botSetupForm.get(ENABLE_MULTILANGUAGE_CONTROL)?.value === true;
    const defaultLanguage: string = this.botSetupForm.get(STEP_3_DEFAULT_LANGUAGE_CONTROL)?.value || 'en-US';
    return multi || !defaultLanguage.toLowerCase().startsWith('en') ? 'flux-general-multi' : 'flux-general-en';
  }

  /** A saved settings.fluxVoice, or Haley when unset or no longer offered. */
  private toFluxVoice(value?: string): FluxVoice {
    return FLUX_VOICE_OPTIONS.find(option => option.value === value)?.value ?? DEFAULT_FLUX_VOICE;
  }

  /** A saved settings.realtimeVoice, or Marin when unset or no longer offered. */
  private toRealtimeVoice(value?: string): RealtimeVoice {
    return REALTIME_VOICE_OPTIONS.find(option => option.value === value)?.value ?? DEFAULT_REALTIME_VOICE;
  }

  /** Render and play the fixed preview line in a Flux voice; clicking the playing voice stops it. */
  toggleFluxVoicePreview(voice: FluxVoice, event?: Event): void {
    this.toggleVoicePreview(voice, () => this._voiceCatalogService.fluxVoicePreview(voice), event);
  }

  /** Render and play the fixed preview line in a Realtime voice; clicking the playing voice stops it. */
  toggleRealtimeVoicePreview(voice: RealtimeVoice, event?: Event): void {
    this.toggleVoicePreview(voice, () => this._voiceCatalogService.realtimeVoicePreview(voice), event);
  }

  private toggleVoicePreview(
    voice: FluxVoice | RealtimeVoice,
    load: () => Observable<{ mime: string; audioBase64: string }>,
    event?: Event
  ): void {
    // The button sits inside the radio row: previewing must not select the voice.
    event?.stopPropagation();
    event?.preventDefault();
    const wasPlaying = this.voicePreviewPlaying === voice;
    this.stopVoicePreview();
    if (wasPlaying || this.voicePreviewLoading) return;

    this.voicePreviewLoading = voice;
    load()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.voicePreviewLoading = null;
          this.playVoicePreview(voice, `data:${result.mime};base64,${result.audioBase64}`);
          this.cd.detectChanges();
        },
        error: () => {
          this.voicePreviewLoading = null;
          this.showSnackbarMessage('Could not load the voice preview.', undefined, 'warn');
          this.cd.detectChanges();
        },
      });
  }

  private playVoicePreview(voice: FluxVoice | RealtimeVoice, src: string): void {
    const audio = new Audio(src);
    this.voicePreviewAudio = audio;
    this.voicePreviewPlaying = voice;
    const done = (): void => {
      if (this.voicePreviewAudio !== audio) return;
      this.voicePreviewAudio = null;
      this.voicePreviewPlaying = null;
      this.cd.detectChanges();
    };
    audio.onended = done;
    audio.onerror = done;
    audio.play().catch(done);
    this.cd.detectChanges();
  }

  private stopVoicePreview(): void {
    if (this.voicePreviewAudio) {
      this.voicePreviewAudio.pause();
      this.voicePreviewAudio = null;
    }
    this.voicePreviewPlaying = null;
  }

  /** languageOptions filtered to what this bot is actually allowed to pick from. */
  get visibleLanguageOptions(): typeof this.languageOptions {
    const isChatBot = this.botSetupForm.get('step2.type')?.value === 'TEXT';
    return this.languageOptions.filter(opt => (this.isAiva2Bot || !opt.aiva2Only) && (isChatBot || !opt.chatOnly));
  }

  /** True when the bot speaks more than one language (enables per-language overrides). */
  get isMultiLanguagePronunciation(): boolean {
    return this.pronunciationBotLanguages.length > 1;
  }

  /** Non-default bot languages available for a per-language override dropdown. */
  get pronunciationOverrideLanguages(): { value: string; label: string }[] {
    return this.pronunciationBotLanguages.filter(lang => lang.value !== this.pronunciationDefaultLanguage);
  }

  addPronunciationEntry(entry?: {
    display?: string;
    spoken?: string;
    variants?: Array<{ language: string; spoken?: string }>;
    previewAudioUrl?: string;
    previewAudioKey?: string;
    voiceSignature?: string;
  }): void {
    // No required validators: blank rows are dropped on save, so a half-filled
    // row must never block the whole Edit Bot submit. previewAudioUrl/Key and
    // voiceSignature cache the last rendered preview for the primary say-it form
    // and are persisted on the default-language variant on save.
    this.pronunciationEntries.push(
      this._formBuilder.group({
        display: [entry?.display ?? ''],
        spoken: [entry?.spoken ?? ''],
        previewAudioUrl: [entry?.previewAudioUrl ?? ''],
        previewAudioKey: [entry?.previewAudioKey ?? ''],
        voiceSignature: [entry?.voiceSignature ?? ''],
        variants: this._formBuilder.array(
          (entry?.variants ?? []).map(variant =>
            this._formBuilder.group({
              language: [variant.language ?? ''],
              spoken: [variant.spoken ?? ''],
            })
          )
        ),
      })
    );
  }

  removePronunciationEntry(index: number): void {
    this.pronunciationEntries.removeAt(index);
  }

  pronunciationEntryVariants(entryIndex: number): UntypedFormArray {
    return this.pronunciationEntries.at(entryIndex).get('variants') as UntypedFormArray;
  }

  addPronunciationVariant(entryIndex: number): void {
    // Default the dropdown to the first override language not already used.
    const used = new Set(this.pronunciationEntryVariants(entryIndex).controls.map(c => c.get('language')?.value));
    const next = this.pronunciationOverrideLanguages.find(lang => !used.has(lang.value));
    this.pronunciationEntryVariants(entryIndex).push(
      this._formBuilder.group({
        language: [next?.value ?? ''],
        spoken: [''],
      })
    );
    // Keep the section open so the new row is visible.
    this.pronunciationExpanded.add(this.pronunciationEntries.at(entryIndex));
  }

  removePronunciationVariant(entryIndex: number, variantIndex: number): void {
    this.pronunciationEntryVariants(entryIndex).removeAt(variantIndex);
  }

  /** True once the bot exists (edit mode) — preview needs a saved bot's provider config. */
  get canPreviewPronunciation(): boolean {
    return this.editMode && !!this.botToEdit?._id;
  }

  /**
   * Render and play a preview of an entry's primary say-it form using the bot's
   * own TTS provider. The server caches the clip; the returned URL/key/signature
   * are stored back on the entry so they persist on save and skip re-rendering
   * when the word is unchanged.
   */
  previewPronunciation(entryIndex: number): void {
    const botId = this.botToEdit?._id;
    if (!botId) return;

    const entry = this.pronunciationEntries.at(entryIndex);
    const spoken = (entry.get('spoken')?.value || '').trim();
    if (!spoken || this.previewingPronunciationIndex !== null) return;

    this.previewingPronunciationIndex = entryIndex;
    this._botCrudService
      .getPronunciationPreview(botId, {
        spoken,
        language: this.pronunciationDefaultLanguage,
        display: (entry.get('display')?.value || '').trim(),
        cachedVoiceSignature: entry.get('voiceSignature')?.value || undefined,
        cachedPreviewAudioUrl: entry.get('previewAudioUrl')?.value || undefined,
        cachedPreviewAudioKey: entry.get('previewAudioKey')?.value || undefined,
      })
      .subscribe({
        next: (result) => {
          this.previewingPronunciationIndex = null;
          if (result.previewAudioUrl) {
            entry.patchValue({
              previewAudioUrl: result.previewAudioUrl,
              previewAudioKey: result.previewAudioKey ?? '',
              voiceSignature: result.voiceSignature ?? '',
            });
            this.playPreviewAudio(
              result.previewAudioUrl,
              () => {
                this.playingPronunciationIndex = entryIndex;
                this.playingVariantKey = null;
              },
              () => {
                this.playingPronunciationIndex = null;
              }
            );
          }
          this.cd.detectChanges();
        },
        error: () => {
          this.previewingPronunciationIndex = null;
          this.cd.detectChanges();
        },
      });
  }

  /** Whether a language-override row is currently rendering its preview. */
  isPreviewingVariant(entryIndex: number, variantIndex: number): boolean {
    return this.previewingVariantKey === `${entryIndex}.${variantIndex}`;
  }

  /** Whether a language-override row's preview clip is currently playing. */
  isPlayingVariant(entryIndex: number, variantIndex: number): boolean {
    return this.playingVariantKey === `${entryIndex}.${variantIndex}`;
  }

  /**
   * Render and play a preview of a single language-override row using that
   * row's language. Unlike the primary form, variant previews are not cached on
   * the entry — they render on demand each time.
   */
  previewPronunciationVariant(entryIndex: number, variantIndex: number): void {
    const botId = this.botToEdit?._id;
    if (!botId) return;
    if (this.previewingPronunciationIndex !== null || this.previewingVariantKey !== null) return;

    const entry = this.pronunciationEntries.at(entryIndex);
    const variant = this.pronunciationEntryVariants(entryIndex).at(variantIndex);
    const spoken = (variant.get('spoken')?.value || '').trim();
    const language = variant.get('language')?.value;
    if (!spoken || !language) return;

    const key = `${entryIndex}.${variantIndex}`;
    this.previewingVariantKey = key;
    this._botCrudService
      .getPronunciationPreview(botId, {
        spoken,
        language,
        display: (entry.get('display')?.value || '').trim(),
      })
      .subscribe({
        next: (result) => {
          this.previewingVariantKey = null;
          if (result.previewAudioUrl) {
            this.playPreviewAudio(
              result.previewAudioUrl,
              () => {
                this.playingVariantKey = key;
                this.playingPronunciationIndex = null;
              },
              () => {
                this.playingVariantKey = null;
              }
            );
          }
          this.cd.detectChanges();
        },
        error: () => {
          this.previewingVariantKey = null;
          this.cd.detectChanges();
        },
      });
  }

  private playPreviewAudio(url: string, onPlay: () => void, onStop: () => void): void {
    const audioEl = this.pronunciationAudioRef?.nativeElement;
    if (!audioEl) {
      onStop();
      return;
    }
    const stop = (): void => {
      onStop();
      this.cd.detectChanges();
    };
    audioEl.onended = stop;
    audioEl.onerror = stop;
    audioEl.src = url;
    audioEl
      .play()
      .then(() => {
        onPlay();
        this.cd.detectChanges();
      })
      .catch(() => {
        /* autoplay/interrupt errors are non-fatal for a preview */
        stop();
      });
  }

  // ── Welcome-audio ↔ pronunciation-dictionary sync ────────────────────────

  /** Build the sync request body from the current (possibly unsaved) form state. */
  private buildWelcomeSyncBody(): {
    items: { type: string; plainText: string }[];
    pronunciationOverrides: NonNullable<BotSetup['pronunciationOverrides']>;
  } {
    const openHours = (this.botSetupForm.get(STEP_3_WELCOME_MESSAGE_CONTROL)?.value || '').trim();
    const offHours = (this.botSetupForm.get(STEP_3_WELCOME_MESSAGE_OFF_HOURS_CONTROL)?.value || '').trim();
    const items: { type: string; plainText: string }[] = [];
    if (openHours) items.push({ type: 'OPEN_HOURS', plainText: openHours });
    if (offHours) items.push({ type: 'OFF_HOURS', plainText: offHours });
    const pronunciationOverrides = this.getPronunciationOverridesPayload(this.botSetupForm.getRawValue()) ?? [];
    return { items, pronunciationOverrides };
  }

  /**
   * Ask the server whether the welcome audio still matches the dictionary and
   * which words are baked in. Read-only — regeneration happens on save.
   */
  checkWelcomeSync(): void {
    const botId = this.botToEdit?._id;
    if (!botId || this.botToEdit?.type !== 'VOICE') return;

    const body = this.buildWelcomeSyncBody();
    if (!body.items.length) {
      this.applyWelcomeSync(null);
      return;
    }

    this._botCrudService.getWelcomeSyncStatus(botId, body).subscribe({
      next: (result) => {
        this.applyWelcomeSync(result);
        this.cd.detectChanges();
      },
      // A sync check is advisory; on failure we simply show no banner/badges.
      error: () => {
        this.applyWelcomeSync(null);
        this.cd.detectChanges();
      },
    });
  }

  /** Re-check (debounced) whenever the dictionary or welcome text changes. */
  private watchWelcomeSync(): void {
    this.welcomeSyncTrigger$
      .pipe(debounceTime(700), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.checkWelcomeSync());

    merge(
      this.botSetupForm.get('step6.pronunciationOverrides')?.valueChanges ?? EMPTY,
      this.botSetupForm.get(STEP_3_WELCOME_MESSAGE_CONTROL)?.valueChanges ?? EMPTY,
      this.botSetupForm.get(STEP_3_WELCOME_MESSAGE_OFF_HOURS_CONTROL)?.valueChanges ?? EMPTY
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.welcomeSyncTrigger$.next());
  }

  /** Fold a sync result into the lookup sets/maps the template reads. */
  private applyWelcomeSync(result: WelcomeSyncResult | null): void {
    this.welcomeSync = result;
    this.welcomeBakedWords = new Set<string>();
    this.welcomePendingWords = new Set<string>();
    this.welcomeSpokenByType = new Map<string, string>();
    for (const item of result?.items ?? []) {
      this.welcomeSpokenByType.set(item.type, item.spokenText);
      const target = item.stale ? this.welcomePendingWords : this.welcomeBakedWords;
      for (const word of item.boundWords ?? []) {
        target.add(word.toLowerCase());
      }
    }
  }

  /** True when at least one welcome greeting is out of sync with the dictionary. */
  get welcomeAudioStale(): boolean {
    return !!this.welcomeSync?.stale;
  }

  /**
   * Per-word tracking for the dictionary panel: 'pending' if the word is in a
   * stale greeting (will bake on save), 'baked' if already in the current audio,
   * or null if the word does not appear in any welcome message.
   */
  welcomeAudioStatus(display: string): 'baked' | 'pending' | null {
    const key = (display || '').trim().toLowerCase();
    if (!key) return null;
    if (this.welcomePendingWords.has(key)) return 'pending';
    if (this.welcomeBakedWords.has(key)) return 'baked';
    return null;
  }

  private setPronunciationOverridesFromBot(bot: BotData): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const overrides = ((bot.settings?.voice as any)?.pronunciationOverrides ?? []) as Array<{
      display: string;
      variants?: Array<{
        language: string;
        spoken?: string;
        previewAudioUrl?: string;
        previewAudioKey?: string;
        voiceSignature?: string;
      }>;
    }>;

    while (this.pronunciationEntries.length) {
      this.pronunciationEntries.removeAt(0);
    }
    this.pronunciationExpanded.clear();

    const defaultLang = this.pronunciationDefaultLanguage;
    overrides.forEach((entry) => {
      // Legacy variants may still carry es-ES; normalize before matching so the
      // language select finds an option instead of hydrating blank (AIVA-2044).
      const variants = (entry.variants ?? []).map(v => ({
        ...v,
        language: normalizeLanguageCode(v.language),
      }));
      // The default-language variant (or the first) is the primary say-it form;
      // everything else becomes a per-language override.
      const primary = variants.find(v => v.language === defaultLang) ?? variants[0];
      const extras = variants
        .filter(v => v !== primary && v.language)
        .map(v => ({ language: v.language, spoken: v.spoken }));
      this.addPronunciationEntry({
        display: entry.display,
        spoken: primary?.spoken ?? '',
        variants: extras,
        // Cached preview lives on the primary (default-language) variant.
        previewAudioUrl: primary?.previewAudioUrl ?? '',
        previewAudioKey: primary?.previewAudioKey ?? '',
        voiceSignature: primary?.voiceSignature ?? '',
      });
      // Reveal overrides that already exist so they aren't hidden on open.
      if (extras.length) {
        this.pronunciationExpanded.add(this.pronunciationEntries.at(this.pronunciationEntries.length - 1));
      }
    });
  }

  private setupAccountSubAccountListeners(): void {
    const accountControl = this.botSetupForm.get('step1.accounts');
    const subAccountControl = this.botSetupForm.get('step1.subAccount');

    if (!accountControl || !subAccountControl) return;

    subAccountControl.disable();
    this.setupAccountChangeListener(accountControl, subAccountControl);
    this.setupSubAccountChangeListener(subAccountControl);
  }

  private setupAccountChangeListener(accountControl: AbstractControl, subAccountControl: AbstractControl): void {
    accountControl.valueChanges.subscribe((accountUID: string) => {
      this.handleAccountChange(accountUID, subAccountControl);
    });
  }

  private handleAccountChange(accountUID: string, subAccountControl: AbstractControl): void {
    if (accountUID === this.accountSearch.sentinelValue) {
      return;
    }
    if (
      this.editMode &&
      (this.isLoadingEditData || (this.botToEdit?.accountUID === accountUID && this.subAccounts.length > 0))
    ) {
      return;
    }

    subAccountControl.reset();
    subAccountControl.disable();
    this.subAccounts = [];
    if (!accountUID) return;

    this._subAccountsService.getSubAccountsByAccount(accountUID).subscribe({
      next: (subs) => {
        this.subAccounts = subs || [];
        this.handleSubAccountsLoaded(subAccountControl);
      },
      error: () => {
        this.showSnackbarMessage('Failed to load sub-accounts for the selected account.');
      },
    });
  }

  private handleSubAccountsLoaded(subAccountControl: AbstractControl): void {
    if (this.subAccounts.length > 0) {
      subAccountControl.enable();
      this.restoreSubAccountFromDraft(subAccountControl);
    } else {
      this.showSnackbarMessage('No sub-accounts found for the selected account.');
    }
  }

  private restoreSubAccountFromDraft(subAccountControl: AbstractControl): void {
    if (this.editMode) return;

    const storedValue = localStorage.getItem(this.LOCAL_STORAGE_KEY);
    if (!storedValue) return;

    try {
      const parsed = JSON.parse(storedValue);
      const storedSubAccount = parsed?.step1?.subAccount;

      if (this.subAccounts.some(sub => sub.subAccountUID === storedSubAccount)) {
        subAccountControl.setValue(storedSubAccount);
      }
    } catch (error) {
      console.warn('Error restoring subAccount from draft:', error);
    }
  }

  private setupSubAccountChangeListener(subAccountControl: AbstractControl): void {
    subAccountControl.valueChanges.subscribe((subAccountId: string) => {
      if (subAccountId && subAccountId !== this.subAccountSearch.sentinelValue) {
        this.handleSubAccountChange(subAccountId);
      }
    });
  }

  private handleSubAccountChange(subAccountId: string): void {
    const selectedSubAccount = this.subAccounts.find(sub => sub.subAccountUID === subAccountId);
    if (!selectedSubAccount?.subAccountUID) return;

    this.loadSmsBotsForCurrentContext(selectedSubAccount);
  }

  private loadSmsBotsForCurrentContext(subAccount: SubAccount): void {
    const isEditingVoiceBot = this.editMode && this.botToEdit?.type === 'VOICE';

    if (isEditingVoiceBot && this.botToEdit?.subAccountUID) {
      this.loadSmsBots(this.botToEdit.accountUID!, this.botToEdit.subAccountUID!);
    } else if (subAccount.subAccountUID) {
      const accountUID = subAccount.accountPreview?.accountUID || this.botSetupForm.get('step1.accounts')?.value;
      this.loadSmsBots(accountUID, subAccount.subAccountUID);
    }
  }

  private loadSmsBots(accountUID: string, subAccountUID: string): void {
    if (!subAccountUID) {
      this.smsBots = [];
      return;
    }

    this._botCrudService.getBots(accountUID, subAccountUID, 'SMS').subscribe({
      next: (bots) => {
        this.smsBots = (bots as BotData[]) || [];

        if (this.pendingFollowUpId) {
          this.setLinkedSmsBotByFollowUpId(this.pendingFollowUpId);
        }

        this.cd.detectChanges();
      },
      error: () => {
        this.smsBots = [];
      },
    });
  }

  buildForm(): FormGroup {
    return this._formBuilder.group({
      step1: this._formBuilder.group(
        {
          accounts: ['', Validators.required],
          subAccount: [''],
        },
        { validators: this.step1Validator() }
      ),
      step2: this._formBuilder.group(
        {
          displayNameBot: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9\-\/\s']+$/)]],
          type: ['', Validators.required],
          origin: [{ value: '', disabled: true }],
        },
        { validators: [this.validateOrigin()] }
      ),
      step3: this._formBuilder.group({
        defaultLanguage: ['', Validators.required],
        welcomeMessage: ['', Validators.required],
        welcomeMessageoffHours: [''],
        voiceFlavor: ['WARM_FRIENDLY'],
        voiceTone: ['HIGH'],
        voiceFriendliness: ['HIGH'],
        voiceProfessionalism: ['MEDIUM'],
        enableRepairCostEstimate: [false],
        enableFinancingEstimate: [false],
        // Nora feature flags (config only; UI shows them only when noraFeatures is true).
        flow: ['AIVA1'],
        voicePipeline: ['DEFAULT'],
        fluxVoice: [DEFAULT_FLUX_VOICE],
        realtimeVoice: [DEFAULT_REALTIME_VOICE],
        callerCanInterrupt: [false],
        mileageCollection: ['OFF'],
      }),
      step4: this._formBuilder.group({
        enableMultilanguage: ['', Validators.required],
      }),
      step5: this._formBuilder.group({
        allowedLanguages: this._formBuilder.group(
          Object.fromEntries(this.languageOptions.map(opt => [opt.controlName, [false]])),
          { validators: [this.allowedLanguagesRequiredIfEnabled()] }
        ),
      }),
      step6: this._formBuilder.group({
        callForwardingPhoneNo: ['', [Validators.required, Validators.pattern(/^\+?[1-9]\d{9,14}(?:x\d+)?$/i)]],
        callForwardingExtension: ['', [Validators.pattern(/^\d*$/)]],
        afterHoursForwardingPhrase: [''],
        allowTransferWithoutCustomer: [false],
        enableVoicemail: [false],
        voicemailConsentMessage: [''],
        voicemailGreeting: [''],
        voicemailMaxDurationSeconds: [120, [Validators.min(30), Validators.max(600)]],
        enableDtmf: [false],
        dtmfMenuPrompt: ['Please make a selection from your keypad.'],
        dtmfMaxRetries: [2, [Validators.min(0), Validators.max(5)]],
        dtmfInputTimeoutMs: [8000, [Validators.min(1000), Validators.max(30000)]],
        dtmfFallbackPrompt: ['We did not receive a keypad entry. Please stay on the line for assistance.'],
        dtmfMenuOptions: this._formBuilder.array([
          this._formBuilder.group({
            digit: ['1', [Validators.required, Validators.pattern(/^[0-9*#]$/)]],
            label: ['Sales'],
            actionType: ['TRANSFER', Validators.required],
            phoneNumber: ['', [Validators.pattern(/^\+?[1-9]\d{9,14}(?:[xX#]\d+)?$/)]],
            extension: ['', [Validators.pattern(/^\d*$/)]],
            flowBranchId: [''],
            prompt: [''],
          }),
          this._formBuilder.group({
            digit: ['2', [Validators.required, Validators.pattern(/^[0-9*#]$/)]],
            label: ['Repeat menu'],
            actionType: ['REPEAT_MENU', Validators.required],
            phoneNumber: ['', [Validators.pattern(/^\+?[1-9]\d{9,14}(?:[xX#]\d+)?$/)]],
            extension: ['', [Validators.pattern(/^\d*$/)]],
            flowBranchId: [''],
            prompt: [''],
          }),
        ]),
        pronunciationOverrides: this._formBuilder.array([]),
      }),
      step7: this._formBuilder.group({
        enableSmsFollowUp: [false],
        linkedVoiceBot: [''],
      }),
      [STEP_8_GROUP]: this._formBuilder.group({
        headerBackgroundColor: ['#EEEEEE', [Validators.pattern(HEX_COLOR_OR_EMPTY_REGEX)]],
        headerTextColor: ['#FFFFFF', [Validators.pattern(HEX_COLOR_OR_EMPTY_REGEX)]],
        headerFont: [SYSTEM_DEFAULT],
        botMessageBubbleColor: ['#f12424', [Validators.pattern(HEX_COLOR_OR_EMPTY_REGEX)]],
        customerMessageBubbleColor: ['#8069DB', [Validators.pattern(HEX_COLOR_OR_EMPTY_REGEX)]],
        chatBubbleBackgroundColor: ['#607785', [Validators.pattern(HEX_COLOR_OR_EMPTY_REGEX)]],
        chatBubbleTextColor: ['#EEEEEE', [Validators.pattern(HEX_COLOR_OR_EMPTY_REGEX)]],
        chatBubbleFont: [SYSTEM_DEFAULT],
        chatBubbleTextSize: ['medium'],
        chatButtonColor: ['#1a2a4a', [Validators.pattern(HEX_COLOR_OR_EMPTY_REGEX)]],
        chatButtonTextColor: ['#FFFFFF', [Validators.pattern(HEX_COLOR_OR_EMPTY_REGEX)]],
        chatButtonLabel: [SCHEDULE_SERVICE, [Validators.maxLength(40)]],
        chatButtonSize: ['medium'],
        chatButtonWidth: [190, [Validators.min(40), Validators.max(500)]],
        chatButtonHeight: [48, [Validators.min(20), Validators.max(200)]],
        buttonFont: [SYSTEM_DEFAULT],
        fontWeight: [SEMIBOLD],
        buttonStyle: [PILL],
        headerLogo: [''],
        buttonPlacement: [BOTTOM_RIGHT, Validators.required],
        marginTop: ['auto'],
        marginBottom: ['20px'],
        marginLeft: ['auto'],
        marginRight: ['40px'],
        chatButtonZIndex: [1000, [Validators.required, Validators.min(0), Validators.max(9999)]],
        chatWindowZIndex: [1000, [Validators.required, Validators.min(0), Validators.max(9999)]],
        advancedMarginControl: [false],
        horizontalOffset: [40],
        verticalOffset: [20],
      }),
      step9: this._formBuilder.group({
        fallbackTeamId: ['', [Validators.maxLength(100)]],
        forceDefaultTeamOnly: [false],
      }),
    });
  }

  validateOrigin(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const type = group.get('type')?.value;
      const originControl = group.get('origin');
      const originValue = originControl?.value;

      if ((type === 'VOICE' || type === 'SMS') && (!originValue || originValue.trim() === '')) {
        originControl?.setErrors({ required: true });
        return { originRequired: true };
      }

      return null;
    };
  }

  step1Validator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const accounts = group.get('accounts')?.value;
      const subAccount = group.get('subAccount')?.value;
      const hasSubs = this.subAccounts.length > 0;

      const errors: ValidationErrors = {
        accountRequired: !accounts,
        subAccountRequired: hasSubs && !subAccount,
        noSubAccounts: !hasSubs && !!accounts,
      };

      const activeErrors = Object.fromEntries(Object.entries(errors).filter(([, isError]) => isError));
      return Object.keys(activeErrors).length ? activeErrors : null;
    };
  }

  setupAutoSave(): void {
    merge(this.botSetupForm.valueChanges, this.botSetupForm.statusChanges)
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        const formValue = this.botSetupForm.getRawValue();
        localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(formValue));
      });
  }

  restoreFormFromStorage(): void {
    const storedValue = localStorage.getItem(this.LOCAL_STORAGE_KEY);
    if (storedValue) {
      try {
        const parsed = JSON.parse(storedValue);
        this.botSetupForm.patchValue(this.normalizeDraftLanguages(parsed) as never);
        this.syncVoicemailControlsForBotType();
      } catch (error) {
        console.warn('Error restoring draft:', error);
      }
    }
  }

  clearDraft(): void {
    localStorage.removeItem(this.LOCAL_STORAGE_KEY);
    this.botSetupForm.reset();
  }

  handleConditionalStep(controlName: string, resetPath: string, setter: (v: boolean) => void, validatorPath?: string): void {
    const control = this.botSetupForm.get(controlName);
    if (!control) return;

    const evaluate = (value: boolean): void => {
      setter(value);
      if (!value && resetPath) {
        this.botSetupForm.get(resetPath)?.reset();
      }
      if (validatorPath) {
        this.botSetupForm.get(validatorPath)?.updateValueAndValidity();
      }
    };
    evaluate(control.value);
    control.valueChanges.subscribe(evaluate);
  }

  markOnlyDefaultLanguage(group: FormGroup, lang: string): void {
    Object.keys(group.controls).forEach((key) => {
      group.get(key)?.setValue(key === lang, { emitEvent: false });
    });
  }

  handleLanguageStepVisibility(): void {
    const enableControl = this.botSetupForm.get(ENABLE_MULTILANGUAGE_CONTROL);
    const allowedLangsGroup = this.botSetupForm.get(ALLOWED_LANGUAGES_CONTROL) as FormGroup;
    const defaultLangControl = this.botSetupForm.get(STEP_3_DEFAULT_LANGUAGE_CONTROL);

    const applyDefaultLanguage = (): void => {
      if (this.editMode) return;
      const defaultLang = defaultLangControl?.value;
      const selectedLang = this.languageOptions.find(lang => lang.value === defaultLang);
      const controlName = selectedLang?.controlName;

      if (controlName && allowedLangsGroup?.get(controlName)) {
        this.markOnlyDefaultLanguage(allowedLangsGroup, controlName);
      }
    };

    const evaluate = (enabled: boolean): void => {
      this.showLanguageStep = enabled;
      if (!enabled) {
        allowedLangsGroup?.reset();
      } else {
        applyDefaultLanguage();
      }
      allowedLangsGroup?.updateValueAndValidity();
    };
    evaluate(enableControl?.value);

    enableControl?.valueChanges.subscribe(enabled => evaluate(enabled));
    defaultLangControl?.valueChanges.subscribe(() => {
      if (enableControl?.value === true) {
        applyDefaultLanguage();
      }
    });
  }

  handleSmsFollowUpVisibility(): void {
    const enableControl = this.botSetupForm.get('step7.enableSmsFollowUp');

    const evaluate = (enabled: boolean): void => {
      this.showSmsFollowUp = enabled;
    };

    evaluate(enableControl?.value);
    enableControl?.valueChanges.subscribe(enabled => evaluate(enabled));
  }

  allowedLanguagesRequiredIfEnabled(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isEnabled = this.botSetupForm?.get(ENABLE_MULTILANGUAGE_CONTROL)?.value;
      if (!isEnabled) return null;

      const groupValue = control.value || {};
      const atLeastOneSelected = Object.values(groupValue).some(val => val === true);
      return atLeastOneSelected ? null : { atLeastOneLanguage: true };
    };
  }

  confirmClearDraft(): void {
    if (confirm('Delete form responses? This action will erase your responses and cannot be undone')) {
      this.clearDraft();
      this.accordion.closeAll();
    }
  }

  private handleSMSOrigin(originControl: AbstractControl | null): void {
    originControl?.disable();
    originControl?.clearValidators();
    originControl?.updateValueAndValidity();

    const selectedSubAccountId = this.botSetupForm.get(SUB_ACCOUNT_CONTROL)?.value;
    const selectedAccountId = this.botSetupForm.get(`${STEP_1_GROUP}.accounts`)?.value;
    const selectedSubAccount = this.subAccounts.find(sub => sub.subAccountUID === selectedSubAccountId);

    const accountUID =
      this.editMode && this.botToEdit
        ? this.botToEdit.accountUID
        : selectedSubAccount?.accountPreview?.accountUID || selectedAccountId;

    const subAccountUID =
      this.editMode && this.botToEdit
        ? this.botToEdit.subAccountUID
        : selectedSubAccount?.subAccountUID || selectedSubAccountId;

    if (!accountUID || !subAccountUID) {
      console.warn('Cannot fetch SMS origin: missing accountUID or subAccountUID', {
        accountUID,
        subAccountUID,
        editMode: this.editMode,
        selectedSubAccount,
      });
      return;
    }

    this._botCrudService.getSmsOrigin(accountUID, subAccountUID).subscribe({
      next: (resp) => {
        originControl?.setValue(resp.phoneNumber);
      },
      error: () => {
        this.showSmsOriginError(originControl);
      },
    });
  }

  handleOriginAutogeneration(): void {
    const typeControl = this.botSetupForm.get(`${STEP_2_GROUP}.type`);
    const nameControl = this.botSetupForm.get(`${STEP_2_GROUP}.displayNameBot`);
    const originControl = this.botSetupForm.get(`${STEP_2_GROUP}.origin`);

    const updateOrigin = (): void => {
      const type = typeControl?.value;
      const name = nameControl?.value || '';
      const originControl = this.botSetupForm.get('step2.origin');

      if (type === 'TEXT') {
        if (name.trim()) {
          const kebabName = name
            .toLowerCase()
            .replace(/[^a-zA-Z0-9()+\-\/ ]/g, '')
            .trim()
            .replace(/\s+/g, '-');

          originControl?.setValue(`chatbot-${kebabName}`);
        } else {
          originControl?.setValue('');
        }

        originControl?.disable();
        originControl?.clearValidators();
      } else if (type === 'VOICE') {
        const current = originControl?.value;
        if (current?.startsWith('chatbot-')) {
          originControl?.setValue('');
        }
        originControl?.enable();
        originControl?.setValidators([Validators.required, Validators.pattern('^[- +()0-9]{10,15}$')]);
      } else if (type === 'SMS') {
        this.handleSMSOrigin(originControl);
      }

      originControl?.updateValueAndValidity();
    };

    typeControl?.valueChanges.subscribe(updateOrigin);
    nameControl?.valueChanges.subscribe(updateOrigin);
    originControl?.valueChanges.subscribe((value: string) => {
      const type = typeControl?.value;
      if ((type === 'VOICE' || type === 'SMS') && value != null) {
        const filtered = value.replace(/[^0-9()+\- ]/g, '');
        if (filtered !== value) {
          originControl.setValue(filtered, { emitEvent: false });
        }
      }
    });
    updateOrigin();
  }

  private handleVoicemailControlsByBotType(): void {
    const typeControl = this.botSetupForm.get(STEP_2_TYPE_CONTROL);
    if (!typeControl) {
      return;
    }

    this.syncVoicemailControlsForBotType(typeControl.value);
    typeControl.valueChanges.subscribe((type: string) => this.syncVoicemailControlsForBotType(type));
  }

  private syncVoicemailControlsForBotType(type?: string): void {
    const step6 = this.botSetupForm.get('step6');
    const botType = type ?? this.botSetupForm.get(STEP_2_TYPE_CONTROL)?.value;
    if (!step6) {
      return;
    }

    const isVoice = botType === 'VOICE';
    const voicemailControls = [
      'enableVoicemail',
      'voicemailConsentMessage',
      'voicemailGreeting',
      'voicemailMaxDurationSeconds',
    ] as const;
    const maxDurationValidators = [Validators.min(30), Validators.max(600)];

    voicemailControls.forEach((controlName) => {
      const control = step6.get(controlName);
      if (!control) {
        return;
      }

      if (isVoice) {
        control.enable({ emitEvent: false });
        if (controlName === 'voicemailMaxDurationSeconds') {
          control.setValidators(maxDurationValidators);
        }
      } else {
        if (controlName === 'voicemailMaxDurationSeconds') {
          control.clearValidators();
        }
        control.disable({ emitEvent: false });
      }

      control.updateValueAndValidity({ emitEvent: false });
    });

    step6.updateValueAndValidity({ emitEvent: false });
  }

  private showSmsOriginError(control: AbstractControl | null): void {
    control?.setValue('');
    this.showSnackbarMessage('It is not possible to continue because Messaging is not enabled for this subAccount.');
  }

  buildSystemKeyMap(subAccount: SubAccount): Record<string, string> {
    return (
      subAccount?.systemKeys?.reduce(
        (acc, item) => {
          acc[item.key] = item.value;
          return acc;
        },
        {} as Record<string, string>
      ) || {}
    );
  }

  formatOfficeSchedule(schedule: { displayName: string; startTime: string; endTime: string }[]): string {
    return schedule
      .map(({ displayName, startTime, endTime }) => `${displayName} ${startTime.slice(0, 5)}-${endTime.slice(0, 5)}`)
      .join(', ');
  }

  getTransferSubAccount(): SubAccount | undefined {
    const selectedSubAccountUID =
      this.botSetupForm.get(SUB_ACCOUNT_CONTROL)?.value || (this.editMode ? this.botToEdit?.subAccountUID : undefined);

    if (!selectedSubAccountUID) {
      return undefined;
    }

    return this.findSubAccount(selectedSubAccountUID);
  }

  getTransferBusinessHoursSummary(): string {
    const subAccount = this.getTransferSubAccount();
    return formatOfficeScheduleSummary(subAccount?.businessInformation?.officeSchedule ?? []);
  }

  getTransferBusinessHoursTimezone(): string {
    const subAccount = this.getTransferSubAccount();
    return subAccount?.businessInformation?.address?.timezone ?? '';
  }

  getTransferBusinessHoursEditorQueryParams(): Record<string, string> | null {
    const selectedSubAccountUID =
      this.editMode && this.botToEdit?.subAccountUID
        ? this.botToEdit.subAccountUID
        : this.botSetupForm.get(SUB_ACCOUNT_CONTROL)?.value;

    if (!selectedSubAccountUID || !this.getTransferSubAccount()) {
      return null;
    }

    return { subAccountUID: selectedSubAccountUID, section: BUSINESS_HOURS_SETTINGS_SECTION };
  }

  private getTransferSetupFields(
    form: ReturnType<BotSetupComponent['botSetupForm']['getRawValue']>
  ): Pick<
    BotSetup,
    | 'enableTransferToLiveAgent'
    | 'transferPhoneNumber'
    | 'transferPhoneExtension'
    | 'enableDtmf'
    | 'dtmfSettings'
    | 'afterHoursForwardingPhrase'
    | 'allowTransferWithoutCustomer'
  > {
    const extension = (form.step6.callForwardingExtension || '').trim();
    const base = {
      enableTransferToLiveAgent: true,
      transferPhoneNumber: form.step6.callForwardingPhoneNo.toString().trim().replace(/\s+/g, ''),
      transferPhoneExtension: extension || undefined,
      afterHoursForwardingPhrase: (form.step6.afterHoursForwardingPhrase || '').trim() || undefined,
      allowTransferWithoutCustomer: form.step6.allowTransferWithoutCustomer ?? false,
    };

    if (form.step2.type !== 'VOICE') {
      return base;
    }

    const menuOptions = (form.step6.dtmfMenuOptions ?? [])
      .filter((option: { digit?: string }) => !!option?.digit)
      .map(
        (option: {
          digit: string;
          label?: string;
          actionType: 'TRANSFER' | 'REPEAT_MENU' | 'FLOW_BRANCH';
          phoneNumber?: string;
          extension?: string;
          prompt?: string;
          flowBranchId?: string;
        }) => {
          const mapped: {
            digit: string;
            actionType: 'TRANSFER' | 'REPEAT_MENU' | 'FLOW_BRANCH';
            label?: string;
            phoneNumber?: string;
            extension?: string;
            prompt?: string;
            flowBranchId?: string;
          } = {
            digit: option.digit,
            actionType: option.actionType,
          };

          const label = option.label?.trim();
          const phoneNumber = option.phoneNumber?.trim();
          const extension = option.extension?.trim();
          const prompt = option.prompt?.trim();
          const flowBranchId = option.flowBranchId?.trim();

          if (label) mapped.label = label;
          if (phoneNumber) mapped.phoneNumber = phoneNumber;
          if (extension) mapped.extension = extension;
          if (prompt) mapped.prompt = prompt;
          if (flowBranchId) mapped.flowBranchId = flowBranchId;

          return mapped;
        }
      );

    const enableDtmf = !!form.step6.enableDtmf;

    return {
      ...base,
      enableDtmf,
      dtmfSettings: enableDtmf
        ? {
            enabled: true,
            menuPrompt: (form.step6.dtmfMenuPrompt || '').trim() || undefined,
            maxRetries: Number(form.step6.dtmfMaxRetries ?? 2),
            inputTimeoutMs: Number(form.step6.dtmfInputTimeoutMs ?? 8000),
            fallbackPrompt: (form.step6.dtmfFallbackPrompt || '').trim() || undefined,
            options: menuOptions,
          }
        : { enabled: false },
    };
  }

  private getVoicemailSetupFields(
    form: ReturnType<BotSetupComponent['botSetupForm']['getRawValue']>
  ): Pick<
    BotSetup,
    'enableVoicemail' | 'voicemailConsentMessage' | 'voicemailGreeting' | 'voicemailMaxDurationSeconds'
  > | null {
    if (form.step2.type !== 'VOICE') {
      return null;
    }

    const maxDuration = Number(form.step6.voicemailMaxDurationSeconds);
    return {
      enableVoicemail: !!form.step6.enableVoicemail,
      voicemailConsentMessage: (form.step6.voicemailConsentMessage || '').trim() || undefined,
      voicemailGreeting: (form.step6.voicemailGreeting || '').trim() || undefined,
      voicemailMaxDurationSeconds: Number.isFinite(maxDuration) && maxDuration > 0 ? maxDuration : 120,
    };
  }

  /**
   * Serialize the pronunciation dictionary for the payload. Each row needs a
   * written word and a default say-it form (stored under the bot's default
   * language and used as the runtime fallback for any language). Optional
   * per-language overrides are appended after it, de-duplicated by language.
   * Empty => [].
   */
  private getPronunciationOverridesPayload(
    form: ReturnType<BotSetupComponent['botSetupForm']['getRawValue']>
  ): BotSetup['pronunciationOverrides'] {
    if (form.step2.type !== 'VOICE') {
      return undefined;
    }
    const defaultLanguage = form.step3.defaultLanguage || 'en-US';

    return (form.step6.pronunciationOverrides ?? [])
      .map(
        (entry: {
          display?: string;
          spoken?: string;
          previewAudioUrl?: string;
          previewAudioKey?: string;
          voiceSignature?: string;
          variants?: Array<{ language?: string; spoken?: string }>;
        }) => {
          const display = (entry.display || '').trim();
          const primarySpoken = (entry.spoken || '').trim();

          const variants: Array<{
            language: string;
            spoken: string;
            previewAudioUrl?: string;
            previewAudioKey?: string;
            voiceSignature?: string;
          }> = [];
          const seenLanguages = new Set<string>();
          // Default-language form first — this is the runtime fallback and the
          // carrier for the cached preview clip of the primary say-it form.
          if (primarySpoken) {
            variants.push({
              language: defaultLanguage,
              spoken: primarySpoken,
              ...(entry.previewAudioUrl ? { previewAudioUrl: entry.previewAudioUrl } : {}),
              ...(entry.previewAudioKey ? { previewAudioKey: entry.previewAudioKey } : {}),
              ...(entry.voiceSignature ? { voiceSignature: entry.voiceSignature } : {}),
            });
            seenLanguages.add(defaultLanguage);
          }
          // Per-language overrides (skip blanks and duplicate languages).
          (entry.variants ?? []).forEach((variant) => {
            const language = (variant.language || '').trim();
            const spoken = (variant.spoken || '').trim();
            if (language && spoken && !seenLanguages.has(language)) {
              variants.push({ language, spoken });
              seenLanguages.add(language);
            }
          });

          return { display, variants };
        }
      )
      .filter((entry: { display: string; variants: unknown[] }) => entry.display && entry.variants.length > 0);
  }

  /**
   * Mileage collection only applies to AIVA2 bots, so an AIVA1 bot always saves OFF even if a mode was
   * picked while it was AIVA2. The engine ignores the setting for AIVA1 anyway; this keeps the data honest.
   */
  private getMileageCollectionToSave(form: BotSetupFormState): MileageCollection {
    return form.step3?.flow === 'AIVA2' ? form.step3?.mileageCollection ?? 'OFF' : 'OFF';
  }

  private getVoiceFlavorSetupFields(
    form: ReturnType<BotSetupComponent['botSetupForm']['getRawValue']>
  ): VoiceFlavorSetupPayload | null {
    if (form.step2.type !== 'VOICE') {
      return null;
    }

    const botTypeControl = this.botSetupForm.get(STEP_2_TYPE_CONTROL);
    const voiceFlavorControl = this.botSetupForm.get('step3.voiceFlavor');
    const voiceAttributeControls = [
      this.botSetupForm.get('step3.voiceTone'),
      this.botSetupForm.get('step3.voiceFriendliness'),
      this.botSetupForm.get('step3.voiceProfessionalism'),
    ];
    // In edit mode, skip sending voice-flavor fields until the dealer changes them,
    // so unrelated bot saves do not overwrite an existing flavor configuration.
    if (
      this.editMode &&
      !botTypeControl?.dirty &&
      !voiceFlavorControl?.dirty &&
      voiceAttributeControls.every(control => !control?.dirty)
    ) {
      return null;
    }

    return {
      voiceFlavor: form.step3.voiceFlavor || 'WARM_FRIENDLY',
      // API expects 0–100 numbers; form controls store VoiceAttributeLevel labels.
      voiceTone: voiceAttributeScore(form.step3.voiceTone),
      voiceFriendliness: voiceAttributeScore(form.step3.voiceFriendliness),
      voiceProfessionalism: voiceAttributeScore(form.step3.voiceProfessionalism),
    };
  }

  loadVoiceFlavorPresets(): void {
    this._botCrudService
      .listVoiceFlavorPresets()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (presets) => {
          this.voiceFlavorPresets = presets.length > 0 ? (presets as VoiceFlavorPreset[]) : FALLBACK_VOICE_FLAVOR_PRESETS;
          this.cd.detectChanges();
        },
        error: () => {
          this.voiceFlavorPresets = FALLBACK_VOICE_FLAVOR_PRESETS;
          this.cd.detectChanges();
        },
      });
  }

  getVoiceTypeLabel(voiceType?: string): string {
    switch (voiceType) {
      case 'MALE':
        return 'BOTS.voiceTypeMale';
      case 'FEMALE':
        return 'BOTS.voiceTypeFemale';
      case 'NEUTRAL':
        return 'BOTS.voiceTypeNeutral';
      default:
        return 'BOTS.voiceTypeNeutral';
    }
  }

  getSelectedVoiceFlavorPreset(): VoiceFlavorPreset | undefined {
    const flavorId = this.botSetupForm.get('step3.voiceFlavor')?.value;
    return this.voiceFlavorPresets.find(preset => preset.id === flavorId);
  }

  onVoiceFlavorChange(): void {
    const preset = this.getSelectedVoiceFlavorPreset();
    if (!preset) {
      return;
    }

    this.botSetupForm.patchValue({
      step3: {
        voiceTone: voiceAttributeLevelFromValue(preset.defaultAttributes.tone),
        voiceFriendliness: voiceAttributeLevelFromValue(preset.defaultAttributes.friendliness),
        voiceProfessionalism: voiceAttributeLevelFromValue(preset.defaultAttributes.professionalism),
      },
    });
  }

  previewSelectedVoiceFlavor(): void {
    if (this.isPlayingVoiceFlavorPreview) {
      return;
    }

    const preset = this.getSelectedVoiceFlavorPreset();
    const sampleText =
      this.botSetupForm.get(STEP_3_WELCOME_MESSAGE_CONTROL)?.value || 'Hello! I\'m AIVA. How can I help you today?';
    if (!preset || !sampleText) {
      return;
    }

    this.isPlayingVoiceFlavorPreview = true;
    this.cd.detectChanges();

    this._botCrudService
      .getWelcomeMessageAudio(sampleText, 'mp3', preset.previewVoice, preset.previewPrompt)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: audioBlob => this.playVoiceFlavorPreview(audioBlob),
        error: () => {
          this.isPlayingVoiceFlavorPreview = false;
          this.cd.detectChanges();
        },
      });
  }

  private playVoiceFlavorPreview(audioBlob: Blob): void {
    const audioUrl = URL.createObjectURL(audioBlob);
    const audioEl = new Audio(audioUrl);
    let objectUrlRevoked = false;
    let destroyed = false;
    let unregisterDestroyCleanup: (() => void) | null = null;
    const revokeObjectUrl = (): void => {
      if (!objectUrlRevoked) {
        URL.revokeObjectURL(audioUrl);
        objectUrlRevoked = true;
      }
    };
    const resetPreviewState = (): void => {
      audioEl.onerror = null;
      audioEl.onended = null;
      audioEl.onpause = null;
      this.isPlayingVoiceFlavorPreview = false;
      revokeObjectUrl();
      unregisterDestroyCleanup?.();
      unregisterDestroyCleanup = null;
      if (!destroyed) {
        this.cd.detectChanges();
      }
    };
    unregisterDestroyCleanup = this.destroyRef.onDestroy(() => {
      destroyed = true;
      audioEl.onplay = null;
      audioEl.onerror = null;
      audioEl.onended = null;
      audioEl.onpause = null;
      audioEl.pause();
      audioEl.src = '';
      this.isPlayingVoiceFlavorPreview = false;
      revokeObjectUrl();
    });
    audioEl.onplay = (): void => {
      this.isPlayingVoiceFlavorPreview = true;
      if (!destroyed) {
        this.cd.detectChanges();
      }
    };
    audioEl.onerror = resetPreviewState;
    audioEl.onended = resetPreviewState;
    audioEl.onpause = resetPreviewState;
    audioEl.play().catch(resetPreviewState);
  }

  submitBotSetup(): BotSetup {
    const form = this.botSetupForm.getRawValue();
    const selectedSub = this.subAccounts.find(sub => sub.subAccountUID === form.step1.subAccount);
    const systemKeyMap = this.buildSystemKeyMap(selectedSub!);
    const welcomeMessageOffHours = (form.step3.welcomeMessageoffHours || '').trim();
    const usersEmails = selectedSub?.notificationProviders?.[0]?.destinations;

    const base: Partial<BotSetup> = {
      accountUID: form.step1.accounts,
      botName: form.step2.displayNameBot,
      botType: form.step2.type,
      botOrigin: form.step2.origin,
      botDefaultLanguage: form.step3.defaultLanguage,
      welcomeMessage: form.step3.welcomeMessage,
      enableMultilanguage: form.step4.enableMultilanguage,
      allowedLanguages: form.step4.enableMultilanguage
        ? this.languageOptions
            // The default language always goes in, even if its tick was lost.
            .filter(opt => form.step5.allowedLanguages?.[opt.controlName] || opt.value === form.step3.defaultLanguage)
            .map(opt => opt.value)
            .join(',')
        : undefined,
      ...this.getTransferSetupFields(form),
      dealershipName: systemKeyMap?.dealerName,
      // Nora feature flags: only sent when the NORA_FEATURES gate is on.
      ...(this.noraFeatures
        ? {
            flow: form.step3.flow,
            voicePipeline: form.step3.voicePipeline,
            fluxVoice: form.step3.fluxVoice ?? DEFAULT_FLUX_VOICE,
            realtimeVoice: form.step3.realtimeVoice ?? DEFAULT_REALTIME_VOICE,
            // AIVA-2183: only a REALTIME or FLUX bot carries it; others keep the default.
            ...(hasCallerInterruptSetting(form.step3.voicePipeline)
              ? { callerCanInterrupt: form.step3.callerCanInterrupt === true }
              : {}),
            mileageCollection: this.getMileageCollectionToSave(form),
          }
        : {}),
    };

    const payload: Partial<BotSetup> = this.editMode
      ? {
          ...base,
          subaccountuid: this.botToEdit?.subAccountUID,
          intentId: this.botToEdit?.intents?.find(i => i.default)?.intentUID,
          serviceId: this.botToEdit?.service?.serviceUID,
          autoForward: !!this.botToEdit?.settings?.forceRedirect,
        }
      : {
          ...base,
          subaccountId: selectedSub?._id,
          subaccountuid: form.step1.subAccount,
          autoForward: false,
          subscriptionid: systemKeyMap?.subscriptionId,
          fallbackOpCode: systemKeyMap?.fallbackOpCode,
          timezone: selectedSub?.businessInformation?.address?.timezone,
          serviceDepartmentHours: this.formatOfficeSchedule(selectedSub?.businessInformation?.officeSchedule || []),
        };

    if (welcomeMessageOffHours) payload.welcomeMessageOffHours = welcomeMessageOffHours;
    if (!this.editMode && usersEmails?.length) payload.usersEmails = usersEmails.join(',');
    this.appendFollowUpPayload(payload, form);
    this.appendVoiceAndSchedulingFields(payload, form, systemKeyMap);

    return payload as BotSetup;
  }

  private appendVoiceAndSchedulingFields(
    payload: Partial<BotSetup>,
    form: ReturnType<BotSetupComponent['botSetupForm']['getRawValue']>,
    systemKeyMap: ReturnType<BotSetupComponent['buildSystemKeyMap']>
  ): void {
    const voicemailFields = this.getVoicemailSetupFields(form);
    if (voicemailFields) {
      Object.assign(payload, voicemailFields);
    }

    const pronunciationOverrides = this.getPronunciationOverridesPayload(form);
    if (pronunciationOverrides !== undefined) {
      payload.pronunciationOverrides = pronunciationOverrides;
    }

    const voiceFlavorFields = this.getVoiceFlavorSetupFields(form);
    if (voiceFlavorFields) {
      Object.assign(payload, voiceFlavorFields);
    }

    this.applyFallbackTeamFields(payload, form, systemKeyMap);
  }

  private applyFallbackTeamFields(
    payload: Partial<BotSetup>,
    form: ReturnType<FormGroup['getRawValue']>,
    systemKeyMap: ReturnType<typeof this.buildSystemKeyMap>
  ): void {
    if (form.step2.type !== 'VOICE' && form.step2.type !== 'SMS' && form.step2.type !== 'TEXT') {
      return;
    }
    const fallbackTeamId = form.step9?.fallbackTeamId?.trim() || systemKeyMap?.fallbackTeamId;
    if (fallbackTeamId) {
      payload.fallbackTeamId = fallbackTeamId;
    } else {
      delete payload.fallbackTeamId;
    }
    payload.forceDefaultTeamOnly = form.step9?.forceDefaultTeamOnly ?? false;
  }

  private collectChatCustomizationFromStep8(): Partial<ChatbotCustomization> | null {
    const customizationStep = this.botSetupForm.get(STEP_8_GROUP);
    if (!customizationStep || customizationStep.disabled) {
      return null;
    }
    const raw = customizationStep.getRawValue() as {
      headerColor: string;
      aivaMessageColor: string;
      userMessageColor: string;
      buttonsColor: string;
      chatButtonColor: string;
      chatButtonTextColor: string;
      chatButtonLabel: string;
      headerLogo: string | null;
    };
    const previewLogo = typeof this.chatCustomizationPreviewLogo === 'string' ? this.chatCustomizationPreviewLogo : null;
    const logoURL = previewLogo || raw.headerLogo || null;

    const entries: [keyof ChatbotCustomization, string | null][] = [
      ['headerColor', raw.headerColor?.trim() || null],
      ['botMessageColor', raw.aivaMessageColor?.trim() || null],
      ['userMessageColor', raw.userMessageColor?.trim() || null],
      ['buttonsColor', raw.buttonsColor?.trim() || null],
      ['logoURL', logoURL],
    ];
    const out = Object.fromEntries(entries.filter(([, v]) => v != null && v !== '')) as Partial<ChatbotCustomization>;
    const buttonConfig = {
      backgroundColor: raw.chatButtonColor?.trim() || undefined,
      textColor: raw.chatButtonTextColor?.trim() || undefined,
      label: (raw.chatButtonLabel ?? '').trim() || undefined,
    };
    if (buttonConfig.backgroundColor || buttonConfig.textColor || buttonConfig.label) {
      out.buttonConfig = buttonConfig;
    }

    return Object.keys(out).length ? out : null;
  }

  private appendFollowUpPayload(payload: Partial<BotSetup>, form: ReturnType<FormGroup['getRawValue']>): void {
    const { enableSmsFollowUp, linkedVoiceBot } = form.step7 || {};

    payload.followUp = !!enableSmsFollowUp;

    if (enableSmsFollowUp && linkedVoiceBot) {
      payload.followUpBotUID = linkedVoiceBot;
      payload.defaultFollowUpId = environment.defaultFollowUpId;
    }
  }

  private syncChatCustomizationAfterSubmit(resp: BotSetup, payload: BotSetup): Observable<void> {
    if (!this.isChatCustomizationStepVisible) {
      return of(void 0);
    }

    const customizationPayload = this.buildChatbotCustomizationPayload();
    if (!customizationPayload) {
      return of(void 0);
    }

    const chatbotConfigId = this.resolveChatCustomizationEntityUID(resp, payload);
    if (!chatbotConfigId) {
      return of(void 0);
    }

    return this._botCrudService.updateChatbotCustomization(chatbotConfigId, customizationPayload).pipe(map(() => void 0));
  }

  private buildChatbotCustomizationPayload(): Partial<ChatbotCustomization> | null {
    const customizationStep = this.botSetupForm.get(STEP_8_GROUP);
    if (!customizationStep) return null;
    // Creación TEXT: step8 deshabilitado (no recolectamos). Edición TEXT: siempre puede sincronizar con el PATCH del chat-engine.
    if (customizationStep.pristine && !this.isTextBotEditSession) return null;
    if (customizationStep.invalid) {
      customizationStep.markAllAsTouched();
      return null;
    }
    const {
      headerBackgroundColor,
      headerTextColor,
      headerFont,
      botMessageBubbleColor,
      customerMessageBubbleColor,
      chatBubbleBackgroundColor,
      chatBubbleTextColor,
      chatBubbleFont,
      chatBubbleTextSize,
      chatButtonColor,
      chatButtonTextColor,
      chatButtonLabel,
      chatButtonSize,
      chatButtonWidth,
      chatButtonHeight,
      buttonFont,
      fontWeight,
      buttonStyle,
      headerLogo,
      buttonPlacement,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      chatButtonZIndex,
      chatWindowZIndex,
    } = customizationStep.getRawValue();
    // `headerLogo` is always kept up-to-date by ChatbotCustomizationComponent
    // (patchLogo patches it on every upload/remove).  The old parent-state
    // `chatCustomizationPreviewLogo` is no longer authoritative.

    let existingCssObj = {};
    if (this.chatbotCustomization?.css) {
      try {
        existingCssObj = JSON.parse(this.chatbotCustomization.css);
      } catch {
        existingCssObj = {};
      }
    }

    const cssObj = {
      ...existingCssObj,
      headerTextColor,
      headerFont,
      chatBubbleBackgroundColor,
      chatBubbleTextColor,
      chatBubbleFont,
      chatBubbleTextSize,
      chatButtonTextColor,
      chatButtonLabel,
      chatButtonSize,
      chatButtonWidth,
      chatButtonHeight,
      chatButtonFont: buttonFont,
      chatButtonFontWeight: fontWeight,
      chatButtonStyle: buttonStyle,
    };

    const entries: [keyof ChatbotCustomization, unknown][] = [
      ['headerColor', headerBackgroundColor],
      ['botMessageColor', botMessageBubbleColor],
      ['userMessageColor', customerMessageBubbleColor],
      ['buttonsColor', chatButtonColor],
      ['logoURL', headerLogo || null],
      ['placement', buttonPlacement],
      ['marginTop', marginTop],
      ['marginBottom', marginBottom],
      ['marginLeft', marginLeft],
      ['marginRight', marginRight],
      ['zIndexButton', chatButtonZIndex],
      ['zIndexChatWindow', chatWindowZIndex],
      ['css', JSON.stringify(cssObj)],
    ];
    const payload = Object.fromEntries(
      entries.filter(([k, v]) => {
        if (k === 'logoURL') {
          return v !== undefined;
        }
        return v !== undefined && v !== null && v !== '';
      })
    ) as Partial<ChatbotCustomization>;

    const buttonConfig = {
      backgroundColor: chatButtonColor?.trim() || undefined,
      textColor: chatButtonTextColor?.trim() || undefined,
      label: (chatButtonLabel ?? '').trim() || undefined,
    };
    if (buttonConfig.backgroundColor || buttonConfig.textColor || buttonConfig.label) {
      payload.buttonConfig = buttonConfig;
    }

    return Object.keys(payload).length ? payload : null;
  }

  private resolveChatCustomizationEntityUID(response: BotSetup, payload: BotSetup): string | null {
    if (this.editMode && this.botToEdit?._id) {
      return this.botToEdit._id;
    }

    const responseWithEntity = response as BotSetup & { _id?: string };
    return responseWithEntity._id ?? response.botId ?? payload.botId ?? null;
  }

  copyEmbeddableCodeToClipboard(): void {
    const embedCode = this.buildEmbeddableCode();
    if (!embedCode) {
      this.showSnackbarMessage(this.translateService.instant('BOT_CUSTOMIZATION.unableGenerateEmbeddableCode'));
      return;
    }

    navigator.clipboard
      .writeText(embedCode)
      .then(() => {
        this.showSnackbarMessage(this.translateService.instant('copyToClipboard'), undefined, 'primary');
      })
      .catch(() => {
        this.showSnackbarMessage(this.translateService.instant('BOT_CUSTOMIZATION.couldNotCopyEmbeddableCode'));
      });
  }

  openChatWidgetFromSetup(): void {
    const origin = this.botToEdit?.service?.origin;
    const chatbotConfigId = this.botToEdit?._id;
    const source = environment.dataSource;

    if (!origin || !chatbotConfigId || !source) {
      this.showSnackbarMessage(this.translateService.instant('BOT_CUSTOMIZATION.unableOpenChatWidgetMissingData'));
      return;
    }

    this.injectWidgetScript(origin, source, chatbotConfigId!);
  }

  private buildEmbeddableCode(): string | null {
    const origin = this.botToEdit?.service?.origin;
    const chatbotConfigId = this.botToEdit?._id;
    const source = environment.dataSource;

    if (!origin || !chatbotConfigId || !source) {
      return null;
    }

    if (!this.isChatCustomizationStepVisible) {
      return `<script
    src="${source}/assets/js/widget.js"
    data-origin="${origin}"
    data-source="${source}"
    chatbot-config-id="${chatbotConfigId}">
</script>`;
    }

    const s8: Record<string, unknown> = this.botSetupForm.get(STEP_8_GROUP)?.getRawValue() ?? {};

    // Helper to get formatted string value or fallback
    const getVal = (val: unknown, fallback: string = ''): string => {
      if (val === null || val === undefined || val === '') {
        return fallback;
      }
      return String(val);
    };

    const headerBackgroundColor = getVal(s8['headerBackgroundColor']);
    const headerTextColor = getVal(s8['headerTextColor']);
    const headerFont = getVal(s8['headerFont']);
    const headerLogo = getVal(s8['headerLogo']); // default → "" (empty string if no logo)

    const botMessageBubbleColor = getVal(s8['botMessageBubbleColor']);
    const customerMessageBubbleColor = getVal(s8['customerMessageBubbleColor']);
    const chatBubbleBackgroundColor = getVal(s8['chatBubbleBackgroundColor']);
    const chatBubbleTextColor = getVal(s8['chatBubbleTextColor']);
    const chatBubbleFont = getVal(s8['chatBubbleFont']);
    const chatBubbleTextSize = getVal(s8['chatBubbleTextSize']);

    const chatButtonColor = getVal(s8['chatButtonColor']);
    const chatButtonTextColor = getVal(s8['chatButtonTextColor']);
    const chatButtonLabel = getVal(s8['chatButtonLabel']);
    const chatButtonSize = getVal(s8['chatButtonSize']);
    const buttonFont = getVal(s8['buttonFont']);
    const fontWeight = getVal(s8['fontWeight']);
    const buttonStyle = getVal(s8['buttonStyle']);
    const buttonPlacement = getVal(s8['buttonPlacement']);
    const horizontalOffset = getVal(s8['horizontalOffset']);
    const verticalOffset = getVal(s8['verticalOffset']);

    // Specially defaulted attributes
    const advancedMarginControl =
      s8['advancedMarginControl'] === true || String(s8['advancedMarginControl']).toLowerCase() === 'true'
        ? 'true'
        : 'false';
    const marginTop = getVal(s8['marginTop'], 'auto');
    const marginRight = getVal(s8['marginRight'], 'auto');
    const marginBottom = getVal(s8['marginBottom'], 'auto');
    const marginLeft = getVal(s8['marginLeft'], 'auto');

    const chatButtonZIndex = getVal(s8['chatButtonZIndex']);
    const chatWindowZIndex = getVal(s8['chatWindowZIndex']);

    return `<script
    src="${source}/assets/js/widget.js"
    data-origin="${origin}"
    data-source="${source}"
    chatbot-config-id="${chatbotConfigId}"

    data-header-bg="${headerBackgroundColor}"
    data-header-text-color="${headerTextColor}"
    data-header-font="${headerFont}"
    data-header-logo="${headerLogo}"

    data-bot-bubble-color="${botMessageBubbleColor}"
    data-customer-bubble-color="${customerMessageBubbleColor}"
    data-chat-bubble-bg="${chatBubbleBackgroundColor}"
    data-chat-bubble-text-color="${chatBubbleTextColor}"
    data-chat-bubble-font="${chatBubbleFont}"
    data-chat-bubble-text-size="${chatBubbleTextSize}"

    data-button-color="${chatButtonColor}"
    data-button-text-color="${chatButtonTextColor}"
    data-button-label="${chatButtonLabel}"
    data-button-size="${chatButtonSize}"
    data-button-font="${buttonFont}"
    data-font-weight="${fontWeight}"
    data-button-style="${buttonStyle}"
    data-button-placement="${buttonPlacement}"
    data-horizontal-offset="${horizontalOffset}"
    data-vertical-offset="${verticalOffset}"
    data-advanced-margin-control="${advancedMarginControl}"
    data-margin-top="${marginTop}"
    data-margin-right="${marginRight}"
    data-margin-bottom="${marginBottom}"
    data-margin-left="${marginLeft}"
    data-button-z-index="${chatButtonZIndex}"
    data-window-z-index="${chatWindowZIndex}">
</script>`;
  }

  private buildEmbedCustomizationAttrs(): string {
    return '';
  }

  private injectWidgetScript(origin: string, source: string, chatbotId: string): void {
    this.removeExistingChatElements();
    const existingScript = document.querySelector('script[src$="widget.js"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.src = 'assets/js/widget.js';
    script.setAttribute('data-origin', origin);
    script.setAttribute('data-source', source);
    script.setAttribute('data-chat-base-url', window.location.origin);
    script.setAttribute('data-embedded', 'true');
    script.setAttribute('chatbot-config-id', chatbotId);

    script.onload = (): void => {
      const event = new Event('showChatWidget');
      window.dispatchEvent(event);
    };

    document.body.appendChild(script);
  }

  private removeExistingChatElements(): void {
    const settingsButton = document.getElementById('aiva-chatbot-widget');
    const overlay = document.getElementById('aiva-chatbot-overlay');
    const chatContainer = document.getElementById('chatContainer');

    if (settingsButton) settingsButton.remove();
    if (overlay) overlay.remove();
    if (chatContainer) chatContainer.remove();
  }

  showSnackbarMessage(message: string, action?: string, color: string = 'warn'): void {
    this.snackBar.open(message, action ?? this.translateService.instant(CLOSE_ACTION_KEY), {
      duration: 5000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: [`snackbar-${color}`],
    });
  }

  handleFinalSubmit(): void {
    if (this.botSetupForm.valid) {
      const payload = this.submitBotSetup();
      const successMessage = this.translateService.instant(
        this.editMode ? 'BOTS.editSuccessMessage' : 'BOTS.botCreatedSuccessMessage'
      );

      this.resolveLogoForPayload()
        .pipe(
          switchMap((logoUrl) => {
            if (this.isChatCustomizationStepVisible && !this.editMode) {
              payload.chatbotCustomization = this.buildChatbotCustomizationForOnboarding(logoUrl);
            }

            return this.editMode && this.botToEdit?._id
              ? this._botCrudService.updateOnboardingBots(this.botToEdit._id, payload)
              : this._botCrudService.createOnboardingBots(payload);
          }),
          switchMap((resp) => {
            this.botCreated = true;
            const entityUID = this.resolveTaggingEntityUID(resp, payload);
            const taggingSync$ = this.syncTaggingRules(entityUID).pipe(
              catchError((error) => {
                console.error('Error syncing tagging rules:', error);
                return of(void 0);
              })
            );
            const customizationSync$ = this.syncChatCustomizationAfterSubmit(resp, payload).pipe(
              catchError((error) => {
                console.error('Error syncing chatbot customization:', error);
                this.showSnackbarMessage(this.translateService.instant('BOT_CUSTOMIZATION.syncFailedNonBlocking'));
                return of(void 0);
              })
            );

            return forkJoin([taggingSync$, customizationSync$]).pipe(map(() => resp));
          })
        )
        .subscribe({
          next: (resp) => {
            this.finalizeSuccessfulSubmit(resp, successMessage);
          },
          error: () => {
            this.showSnackbarMessage(
              this.translateService.instant(this.editMode ? 'ERRORS.botUpdateFailed' : 'ERRORS.botCreateFailed')
            );
          },
        });
    } else {
      this.botSetupForm.markAllAsTouched();
    }
  }

  /**
   * Resolves the logo value from the step8 form for inclusion in the onboarding payload.
   * - Existing http/https URL  → returned as-is (no upload needed).
   * - data: URL (new FileReader upload) → returned as-is.
   *   TODO: when a media-upload endpoint is available, replace the data: branch
   *   with an HTTP upload call and return the resulting CDN URL instead.
   * - null / empty / non-TEXT bot → returns null.
   */
  private resolveLogoForPayload(): Observable<string | null> {
    if (!this.isChatCustomizationStepVisible) {
      return of(null);
    }
    const logoValue = this.botSetupForm.get(`${STEP_8_GROUP}.headerLogo`)?.value;
    if (typeof logoValue === 'string' && logoValue.length > 0) {
      return of(logoValue);
    }
    return of(null);
  }

  /**
   * Builds the chatbotCustomization nested object for the onboarding PUT payload.
   * Reads all 27 step8 form controls and maps them to ChatbotCustomization fields
   * (camelCase, matching the existing payload convention).
   */
  private buildChatbotCustomizationForOnboarding(logoUrl: string | null): ChatbotCustomization {
    const s8 = this.botSetupForm.get(STEP_8_GROUP)!.getRawValue();

    const customization: Partial<ChatbotCustomization> = {
      headerBackgroundColor: s8.headerBackgroundColor,
      headerTextColor: s8.headerTextColor,
      headerFont: s8.headerFont,
      botMessageBubbleColor: s8.botMessageBubbleColor,
      customerMessageBubbleColor: s8.customerMessageBubbleColor,
      chatBubbleBackgroundColor: s8.chatBubbleBackgroundColor,
      chatBubbleTextColor: s8.chatBubbleTextColor,
      chatBubbleFont: s8.chatBubbleFont,
      chatBubbleTextSize: s8.chatBubbleTextSize,
      chatButtonColor: s8.chatButtonColor,
      chatButtonTextColor: s8.chatButtonTextColor,
      chatButtonLabel: s8.chatButtonLabel,
      chatButtonSize: s8.chatButtonSize,
      buttonFont: s8.buttonFont,
      fontWeight: s8.fontWeight,
      buttonStyle: s8.buttonStyle,
      buttonPlacement: s8.buttonPlacement,
      horizontalOffset: s8.horizontalOffset,
      verticalOffset: s8.verticalOffset,
      advancedMarginControl: s8.advancedMarginControl,
      marginTop: s8.marginTop,
      marginRight: s8.marginRight,
      marginBottom: s8.marginBottom,
      marginLeft: s8.marginLeft,
      chatButtonZIndex: s8.chatButtonZIndex,
      chatWindowZIndex: s8.chatWindowZIndex,
    };

    if (logoUrl) {
      customization.headerLogo = logoUrl;
    }

    return customization as ChatbotCustomization;
  }

  /**
   * Same reason as the Nora flags in finalizeSuccessfulSubmit: the onboarding
   * response is not the full bot, and the list reopens the modal from this
   * shared object. Without this, a saved language / welcome change reopened
   * showing the previous values until the page was reloaded.
   */
  private syncSavedLanguageSettings(form: BotSetupFormState): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const settings = this.botToEdit?.settings as any;
    if (!settings) return;

    const multilanguage = form.step4?.enableMultilanguage === true;
    settings.languageCode = form.step3?.defaultLanguage;
    settings.detectLanguage = multilanguage;
    settings.allowedLanguages = multilanguage
      ? this.languageOptions.filter(opt => form.step5?.allowedLanguages?.[opt.controlName]).map(opt => opt.value)
      : [];

    const welcome = form.step3?.welcomeMessage ?? '';
    const offHours = (form.step3?.welcomeMessageoffHours ?? '').trim();
    settings.welcomeMessage = welcome;
    // An audio welcome keeps its readable copy in plainText (see getWelcomeMessage).
    const withText = (entry: { plainText?: string; message?: string }, text: string): void => {
      if (entry.plainText !== undefined) entry.plainText = text;
      else entry.message = text;
    };
    const entries: { type: string; plainText?: string; message?: string }[] = settings.welcomeMessages ?? [];
    const open = entries.find(entry => entry.type === 'OPEN_HOURS');
    const off = entries.find(entry => entry.type === 'OFF_HOURS');
    if (open) withText(open, welcome);
    if (off && offHours) withText(off, offHours);
    if (!off && offHours) entries.push({ type: 'OFF_HOURS', message: offHours });
    settings.welcomeMessages = offHours ? entries : entries.filter(entry => entry.type !== 'OFF_HOURS');
  }

  private finalizeSuccessfulSubmit(resp: BotSetup, successMessage: string): void {
    if (!this.editMode) {
      this.clearDraft();
    } else {
      localStorage.removeItem(this.LOCAL_STORAGE_KEY);
      const form = this.botSetupForm.getRawValue();
      if (this.botToEdit?.settings?.nlp?.contextualFunctions?.forwardToLiveAgent) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.botToEdit.settings.nlp.contextualFunctions.forwardToLiveAgent as any).allowTransferWithoutCustomer =
          form.step6?.allowTransferWithoutCustomer ?? false;
      }
      const botType = this.botToEdit?.type;
      if ((botType === 'VOICE' || botType === 'SMS' || botType === 'TEXT') && this.botToEdit?.settings) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.botToEdit.settings as any).forceDefaultTeamOnly = form.step9?.forceDefaultTeamOnly ?? false;
      }
      this.syncSavedLanguageSettings(form);
      this.syncSavedNoraSettings(form);
    }
    this.showSnackbarMessage(successMessage, undefined, 'primary');
    setTimeout(() => {
      this.dialogRef?.close(resp);
    }, 5000);
  }

  /**
   * Sync the Nora feature flags back onto the shared bot object so reopening
   * the modal reflects the just-saved values (the onboarding response does not
   * return the full bot). Only when the NORA_FEATURES gate sent them.
   */
  private syncSavedNoraSettings(form: BotSetupFormState): void {
    if (!this.noraFeatures || !this.botToEdit?.settings) return;
    this.botToEdit.settings.flow = form.step3?.flow;
    this.botToEdit.settings.voicePipeline = form.step3?.voicePipeline;
    this.botToEdit.settings.fluxVoice = form.step3?.fluxVoice ?? DEFAULT_FLUX_VOICE;
    this.botToEdit.settings.realtimeVoice = form.step3?.realtimeVoice ?? DEFAULT_REALTIME_VOICE;
    if (hasCallerInterruptSetting(form.step3?.voicePipeline)) {
      this.botToEdit.settings.callerCanInterrupt = form.step3?.callerCanInterrupt === true;
    }
    this.botToEdit.settings.mileageCollection = this.getMileageCollectionToSave(form);
  }

  closeModal(): void {
    if (this.dialogRef) {
      this.resetChatbotCustomizationStep();
      this.dialogRef.close();
    }
  }

  setStep(index: number): void {
    this.step.set(index);
  }

  nextStep(): void {
    const currentStep = this.step();
    if (currentStep === 4 && !this.showLanguageStep) {
      this.step.update(() => 6);
    } else {
      this.step.update(i => i + 1);
    }
  }

  prevStep(): void {
    const currentStep = this.step();
    if (currentStep === 6 && !this.showLanguageStep) {
      this.step.set(4);
    } else {
      this.step.update(i => i - 1);
    }
  }

  isStepValid(stepIndex: number): boolean {
    const stepName = `step${stepIndex + 1}`;
    return this.botSetupForm.get(stepName)?.valid ?? false;
  }

  get isVoiceFollowUpStepVisible(): boolean {
    return this.editMode && this.botSetupForm.get(STEP_2_TYPE_CONTROL)?.value === 'VOICE';
  }

  get isTeamSchedulingStepVisible(): boolean {
    const type = this.botSetupForm.get(STEP_2_TYPE_CONTROL)?.value;
    return type === 'VOICE' || type === 'SMS' || type === 'TEXT';
  }

  get isChatCustomizationStepVisible(): boolean {
    return this.isTextBotEditSession;
  }

  get isChatToolsVisible(): boolean {
    return this.isTextBotEditSession;
  }

  get allowedDomains(): string[] {
    return this.uniqueAllowedDomains(this.botToEdit?.settings?.channel?.chatbot?.cors ?? []);
  }

  get nonWildcardAllowedDomains(): string[] {
    return this.allowedDomains.filter(domain => domain !== WILDCARD_DOMAIN);
  }

  get isWildcardAllowedDomainEnabled(): boolean {
    return this.allowedDomains.includes(WILDCARD_DOMAIN);
  }

  get shouldShowEmptyAllowedDomainsWarning(): boolean {
    return this.allowedDomains.length === 0;
  }

  addAllowedDomain(): void {
    const normalizedDomain = this.normalizeAllowedDomain(this.allowedDomainControl.value);
    if (!normalizedDomain) {
      this.setAllowedDomainValidationError(INVALID_ALLOWED_DOMAIN_URL_ERROR, 'invalidUrl');
      return;
    }

    if (this.allowedDomains.includes(normalizedDomain)) {
      this.setAllowedDomainValidationError(DUPLICATE_ALLOWED_DOMAIN_ERROR, 'duplicate');
      return;
    }

    this.persistAllowedDomains([...this.allowedDomains, normalizedDomain], () => {
      this.allowedDomainControl.reset('');
      this.clearAllowedDomainValidationError();
    });
  }

  removeAllowedDomain(domain: string): void {
    this.persistAllowedDomains(this.allowedDomains.filter(currentDomain => currentDomain !== domain));
  }

  toggleWildcardAllowedDomain(enabled: boolean): void {
    const nextDomains = enabled
      ? [...this.allowedDomains, WILDCARD_DOMAIN]
      : this.allowedDomains.filter(domain => domain !== WILDCARD_DOMAIN);

    this.persistAllowedDomains(nextDomains);
  }

  clearAllowedDomainValidationError(): void {
    if (!this.allowedDomainValidationError) {
      return;
    }

    this.allowedDomainValidationError = '';
    this.allowedDomainControl.setErrors(null);
  }

  private persistAllowedDomains(nextDomains: string[], onSuccess?: () => void): void {
    if (!this.botToEdit?._id || this.isSavingAllowedDomains) {
      return;
    }

    this.isSavingAllowedDomains = true;
    const cors = this.uniqueAllowedDomains(nextDomains);

    this._botCrudService
      .updateBotAllowedDomains(this.botToEdit, cors)
      .pipe(
        finalize(() => {
          this.isSavingAllowedDomains = false;
          this.cd.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (updatedBot) => {
          this.botToEdit = {
            ...updatedBot,
            settings: {
              ...updatedBot.settings,
              channel: {
                ...updatedBot.settings?.channel,
                chatbot: {
                  ...updatedBot.settings?.channel?.chatbot,
                  cors,
                },
              },
            },
          };
          this.clearAllowedDomainValidationError();
          onSuccess?.();
        },
        error: () => {
          this.setAllowedDomainValidationError(SAVE_ALLOWED_DOMAINS_ERROR, 'saveFailed');
        },
      });
  }

  private normalizeAllowedDomain(value: string): string | null {
    const trimmedValue = value.trim();
    if (!trimmedValue || trimmedValue === WILDCARD_DOMAIN) {
      return null;
    }

    try {
      const url = new URL(trimmedValue);
      if (!['http:', 'https:'].includes(url.protocol) || !url.hostname) {
        return null;
      }

      return url.origin;
    } catch {
      return null;
    }
  }

  private uniqueAllowedDomains(domains: string[]): string[] {
    return Array.from(new Set(domains.map(domain => domain.trim()).filter(Boolean)));
  }

  private setAllowedDomainValidationError(message: string, errorKey: string): void {
    this.allowedDomainValidationError = message;
    this.allowedDomainControl.setErrors({ [errorKey]: true });
    this.allowedDomainControl.markAsTouched();
    this.cd.markForCheck();
  }

  private get isTextBotEditSession(): boolean {
    return this.editMode && this.botSetupForm.get(STEP_2_TYPE_CONTROL)?.value === 'TEXT';
  }

  get botStatusLabelKey(): 'active' | 'inactive' {
    return this.botToEdit?.settings?.forceRedirect ? 'inactive' : 'active';
  }

  onRepairCostEstimateToggle(event: MatSlideToggleChange): void {
    const botId = this.botToEdit?._id;
    if (!botId || !this.editMode) {
      event.source.checked = false;
      this.botSetupForm.get(STEP_3_ENABLE_REPAIR_COST_ESTIMATE_CONTROL)?.setValue(false, { emitEvent: false });
      return;
    }

    this.repairCostEstimateUpdating = true;
    const request$ = event.checked
      ? this._botCrudService.enableRepairCostEstimate(botId)
      : this._botCrudService.disableRepairCostEstimate(botId);

    request$
      .pipe(
        finalize(() => {
          this.repairCostEstimateUpdating = false;
          this.cd.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (resp) => {
          if (resp.bot) {
            this.botToEdit = { ...this.botToEdit, ...resp.bot };
          }
          this.botSetupForm.get(STEP_3_ENABLE_REPAIR_COST_ESTIMATE_CONTROL)?.setValue(!!resp.enabled, { emitEvent: false });
          this.showSnackbarMessage(
            this.translateService.instant(
              resp.enabled ? 'BOTS.repairCostEstimateEnabled' : 'BOTS.repairCostEstimateDisabled'
            ),
            undefined,
            'primary'
          );
        },
        error: () => {
          event.source.checked = !event.checked;
          this.botSetupForm.get(STEP_3_ENABLE_REPAIR_COST_ESTIMATE_CONTROL)?.setValue(!event.checked, { emitEvent: false });
          this.showSnackbarMessage(this.translateService.instant('BOTS.repairCostEstimateUpdateFailed'));
        },
      });
  }

  private loadRepairCostEstimateStatus(): void {
    const botId = this.botToEdit?._id;
    if (!this.editMode || !botId) {
      return;
    }

    this._botCrudService
      .getRepairCostEstimateStatus(botId)
      .pipe(
        catchError(() => {
          const fromBot = (this.botToEdit?.intents || []).some(
            intent => intent.displayName === REPAIR_COST_ESTIMATE_INTENT_DISPLAY_NAME
          );
          return of({ enabled: fromBot });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((status) => {
        this.botSetupForm.get(STEP_3_ENABLE_REPAIR_COST_ESTIMATE_CONTROL)?.setValue(!!status.enabled, { emitEvent: false });
        this.cd.markForCheck();
      });
  }

  /** AIVA-1768 */
  onFinancingEstimateToggle(event: MatSlideToggleChange): void {
    const botId = this.botToEdit?._id;
    if (!botId || !this.editMode) {
      event.source.checked = false;
      this.botSetupForm.get(STEP_3_ENABLE_FINANCING_ESTIMATE_CONTROL)?.setValue(false, { emitEvent: false });
      return;
    }

    this.financingEstimateUpdating = true;
    const request$ = event.checked
      ? this._botCrudService.enableFinancingEstimate(botId)
      : this._botCrudService.disableFinancingEstimate(botId);

    request$
      .pipe(
        finalize(() => {
          this.financingEstimateUpdating = false;
          this.cd.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (resp) => {
          if (resp.bot) {
            this.botToEdit = { ...this.botToEdit, ...resp.bot };
          }
          this.botSetupForm.get(STEP_3_ENABLE_FINANCING_ESTIMATE_CONTROL)?.setValue(!!resp.enabled, { emitEvent: false });
          this.showSnackbarMessage(
            this.translateService.instant(resp.enabled ? 'BOTS.financingEstimateEnabled' : 'BOTS.financingEstimateDisabled'),
            undefined,
            'primary'
          );
        },
        error: () => {
          event.source.checked = !event.checked;
          this.botSetupForm.get(STEP_3_ENABLE_FINANCING_ESTIMATE_CONTROL)?.setValue(!event.checked, { emitEvent: false });
          this.showSnackbarMessage(this.translateService.instant('BOTS.financingEstimateUpdateFailed'));
        },
      });
  }

  /** AIVA-1768 */
  private loadFinancingEstimateStatus(): void {
    const botId = this.botToEdit?._id;
    if (!this.editMode || !botId) {
      return;
    }

    this._botCrudService
      .getFinancingEstimateStatus(botId)
      .pipe(
        catchError(() => of({ enabled: this.botToEdit?.financingEstimateEnabled === true })),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((status) => {
        this.botSetupForm.get(STEP_3_ENABLE_FINANCING_ESTIMATE_CONTROL)?.setValue(!!status.enabled, { emitEvent: false });
        this.cd.markForCheck();
      });
  }

  onBotStatusToggle(event: MatSlideToggleChange): void {
    const bot = this.botToEdit;
    if (!bot?._id) {
      return;
    }

    const nextForceRedirect = !event.checked;

    const pipeline$: Observable<unknown> = nextForceRedirect
      ? this.dialog
          .open(ConfirmDialogComponent, { data: this.buildForceRedirectConfirmDialogData() })
          .afterClosed()
          .pipe(
            switchMap((confirmed: boolean) => {
              if (!confirmed) {
                event.source.checked = true;
                return of(void 0);
              }
              return this.persistBotForceRedirect$(bot, true);
            })
          )
      : this.persistBotForceRedirect$(bot, false);

    pipeline$
      .pipe(
        finalize(() => this.cd.markForCheck()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private buildForceRedirectConfirmDialogData(): { title: string; message: string; confirmButtonText: string } {
    return {
      title: this.translateService.instant('BOTS.forceRedirectConfirmTitle'),
      message: this.translateService.instant('BOTS.forceRedirectConfirmMessage'),
      confirmButtonText: this.translateService.instant('BOTS.forceRedirectConfirmAction'),
    };
  }

  private persistBotForceRedirect$(bot: BotData, forceRedirect: boolean): Observable<BotData> {
    const payload: Partial<BotData> = { ...bot, settings: { ...bot.settings, forceRedirect } };
    return this._botCrudService.editBot(bot._id!, payload).pipe(
      tap(() => {
        if (!bot.settings) {
          bot.settings = {};
        }
        bot.settings.forceRedirect = forceRedirect;
      }),
      catchError((error: unknown) => {
        console.error('Error updating bot:', error);
        return EMPTY;
      })
    );
  }

  goToNextFromTransferStep(): void {
    if (this.isVoiceFollowUpStepVisible) {
      this.setStep(7);
      return;
    }

    if (this.isTeamSchedulingStepVisible) {
      this.setStep(9);
      return;
    }

    if (this.isChatCustomizationStepVisible) {
      this.setStep(8);
      return;
    }

    if (this.isTeamSchedulingStepVisible) {
      this.setStep(9);
    }
  }

  goToNextFromSmsFollowUpStep(): void {
    if (this.isTeamSchedulingStepVisible) {
      this.setStep(9);
      return;
    }
    this.nextStep();
  }

  goToPreviousFromTeamSchedulingStep(): void {
    if (this.isVoiceFollowUpStepVisible) {
      this.setStep(7);
      return;
    }
    this.setStep(6);
  }

  goToPreviousFromChatCustomizationStep(): void {
    this.setStep(6);
  }

  removeGeneralTag(tag: string): void {
    this.selectedGeneralTags = this.selectedGeneralTags.filter(selectedTag => selectedTag !== tag);
    this.reconcileIdentifierRuleTags();
  }

  handleGeneralTagsChange(tags: string[]): void {
    this.selectedGeneralTags = tags;
    this.reconcileIdentifierRuleTags();
  }

  onAutomaticTaggingRulesPanelOpened(): void {
    this.setStep(2);
    this.loadTagDefinitions();
  }

  get availableIdentifierTagOptions(): string[] {
    return this.generalTagOptions.filter(tag => !this.selectedGeneralTags.includes(tag));
  }

  getIdentifierTagOptions(ruleIndex: number): string[] {
    const selectedTagsInOtherRules = new Set(
      this.identifierTagRules
        .filter((_, index) => index !== ruleIndex)
        .map(rule => rule.tag)
        .filter((tag): tag is string => !!tag)
    );

    return this.availableIdentifierTagOptions.filter(tag => !selectedTagsInOtherRules.has(tag));
  }

  isIdentifierAddDisabled(rule: IdentifierTagRule): boolean {
    const normalizedCandidate = this.normalizeIdentifierPhone(rule.currentIdentifier);
    return !rule.tag || !normalizedCandidate || rule.identifiers.includes(normalizedCandidate);
  }

  addIdentifierRule(): void {
    if (!this.canAddIdentifierRule) return;

    this.identifierTagRules = [...this.identifierTagRules, this.buildIdentifierTagRule()];
  }

  removeIdentifierRule(index: number): void {
    this.identifierTagRules = this.identifierTagRules.filter((_, ruleIndex) => ruleIndex !== index);
  }

  addIdentifierToRule(index: number): void {
    const rule = this.identifierTagRules[index];
    if (!rule || this.isIdentifierAddDisabled(rule)) return;

    const normalizedValue = this.normalizeIdentifierPhone(rule.currentIdentifier);
    if (!normalizedValue || rule.identifiers.includes(normalizedValue)) return;

    this.patchIdentifierRule(index, {
      identifiers: [...rule.identifiers, normalizedValue],
      currentIdentifier: '',
    });
  }

  removeIdentifierFromRule(index: number, identifier: string): void {
    const rule = this.identifierTagRules[index];
    if (!rule) return;

    this.patchIdentifierRule(index, {
      identifiers: rule.identifiers.filter(item => item !== identifier),
    });
  }

  get canAddIdentifierRule(): boolean {
    return this.identifierTagRules.length < this.availableIdentifierTagOptions.length;
  }

  private buildIdentifierTagRule(): IdentifierTagRule {
    return {
      id: this.nextIdentifierTagRuleId++,
      tag: null,
      currentIdentifier: '',
      identifiers: [],
    };
  }

  patchIdentifierRule(index: number, patch: Partial<IdentifierTagRule>): void {
    const rule = this.identifierTagRules[index];
    if (!rule) return;

    this.identifierTagRules = this.identifierTagRules.map((currentRule, ruleIndex) =>
      ruleIndex === index ? { ...currentRule, ...patch } : currentRule
    );
  }

  private reconcileIdentifierRuleTags(): void {
    const blockedByGeneralTags = new Set(this.selectedGeneralTags);
    const alreadyUsed = new Set<string>();

    this.identifierTagRules = this.identifierTagRules.map((rule) => {
      if (!rule.tag) return rule;

      // Keep identifier rules unique and never duplicated with general tags.
      if (blockedByGeneralTags.has(rule.tag) || alreadyUsed.has(rule.tag)) {
        return { ...rule, tag: null };
      }

      alreadyUsed.add(rule.tag);
      return rule;
    });
  }

  isPhoneIdentifier(value: string): boolean {
    return !!this.normalizeIdentifierPhone(value);
  }

  private normalizeIdentifierPhone(value: string): string | null {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    const sanitizedValue = trimmedValue.replace(/[\s()-]/g, '');
    if (!PHONE_IDENTIFIER_WITH_COUNTRY_CODE_AND_TEN_DIGITS_REGEX.test(sanitizedValue)) {
      return null;
    }

    return sanitizedValue;
  }

  private resolveTaggingEntityUID(response: BotSetup, payload: BotSetup): string | null {
    if (this.editMode && this.botToEdit?._id) {
      return this.botToEdit._id;
    }

    const responseWithEntity = response as BotSetup & { _id?: string; entityUID?: string };
    return responseWithEntity._id ?? responseWithEntity.entityUID ?? response.botId ?? payload.botId ?? null;
  }

  private syncTaggingRules(entityUID: string | null): Observable<void> {
    if (!entityUID) {
      return of(void 0);
    }

    const desiredRules = this.buildTaggingRulesPayload(entityUID);

    if (!this.editMode) {
      return this.createTaggingRules(desiredRules);
    }

    return this._botCrudService
      .getTaggingRules({
        provider: TAGGING_PROVIDER,
        entityType: TAGGING_ENTITY_TYPE,
        entityUID,
      })
      .pipe(
        switchMap((existingRules: TaggingRule[]) => this.deleteTaggingRules(existingRules)),
        switchMap(() => this.createTaggingRules(desiredRules))
      );
  }

  private buildTaggingRulesPayload(entityUID: string): TaggingRulePayload[] {
    return [...this.buildGeneralTaggingRules(entityUID), ...this.buildIdentifierTaggingRules(entityUID)];
  }

  private buildGeneralTaggingRules(entityUID: string): TaggingRulePayload[] {
    const uniqueGeneralTags = this.normalizeUniqueValues(this.selectedGeneralTags);
    return uniqueGeneralTags.map(
      (tag): TaggingRulePayload => ({
        name: tag,
        provider: TAGGING_PROVIDER,
        entityUID,
        entityType: TAGGING_ENTITY_TYPE,
        tagAll: true,
        rules: [],
      })
    );
  }

  private buildIdentifierTaggingRules(entityUID: string): TaggingRulePayload[] {
    return this.identifierTagRules
      .filter(rule => !!rule.tag)
      .map((rule): TaggingRulePayload | null => {
        const normalizedIdentifiers = this.normalizeUniqueValues(rule.identifiers);

        if (normalizedIdentifiers.length === 0 || !rule.tag) {
          return null;
        }

        return {
          name: rule.tag,
          provider: TAGGING_PROVIDER,
          entityUID,
          entityType: TAGGING_ENTITY_TYPE,
          tagAll: false,
          rules: [
            {
              variableName: TAGGING_IDENTIFIER_VARIABLE,
              valueList: normalizedIdentifiers,
            },
          ],
        };
      })
      .filter((rule): rule is TaggingRulePayload => !!rule);
  }

  private loadTaggingRules(entityUID: string): void {
    this._botCrudService
      .getTaggingRules({
        provider: TAGGING_PROVIDER,
        entityType: TAGGING_ENTITY_TYPE,
        entityUID,
      })
      .subscribe({
        next: (rules: TaggingRule[]) => {
          this.applyLoadedTaggingRules(rules);
        },
        error: (error) => {
          console.error('Error loading tagging rules:', error);
        },
      });
  }

  private loadTagDefinitions(): void {
    if (this.isLoadingTagDefinitions) return;
    this.isLoadingTagDefinitions = true;

    this._botCrudService
      .getTagDefinitions({
        provider: TAGGING_PROVIDER,
        entityType: TAGGING_ENTITY_TYPE,
      })
      .pipe(finalize(() => (this.isLoadingTagDefinitions = false)))
      .subscribe({
        next: (definitions: TagDefinition[]) => {
          const tagsFromApi = this.extractActiveTagDefinitionNames(definitions);
          this.generalTagOptions = [...new Set(tagsFromApi)];
          this.selectedGeneralTags = this.selectedGeneralTags.filter(tag => this.generalTagOptions.includes(tag));
          this.reconcileIdentifierRuleTags();
          this.cd.detectChanges();
        },
        error: (error) => {
          console.error('Error loading tag definitions:', error);
        },
      });
  }

  private createTaggingRules(rules: TaggingRulePayload[]): Observable<void> {
    if (rules.length === 0) {
      return of(void 0);
    }

    return forkJoin(rules.map(rule => this._botCrudService.createTaggingRule(rule))).pipe(map(() => void 0));
  }

  private deleteTaggingRules(existingRules: TaggingRule[]): Observable<void> {
    const deleteRequests = existingRules
      .map(rule => this.getTaggingRuleId(rule))
      .filter((ruleId): ruleId is string => !!ruleId)
      .map(ruleId => this._botCrudService.deleteTaggingRule(ruleId));

    if (deleteRequests.length === 0) {
      return of(void 0);
    }

    return forkJoin(deleteRequests).pipe(map(() => void 0));
  }

  private getTaggingRuleId(rule: TaggingRule): string | null {
    return rule._id ?? rule.id ?? rule.uid ?? null;
  }

  private applyLoadedTaggingRules(rules: TaggingRule[]): void {
    const activeRules = rules.filter(rule => !rule.deleted);
    const generalTags = this.extractGeneralTagNames(activeRules);
    const identifierRules = this.extractIdentifierTagRules(activeRules);
    const mergedTagOptions = [
      ...new Set([...this.generalTagOptions, ...generalTags, ...identifierRules.map(rule => rule.tag ?? '')]),
    ].filter(Boolean);

    this.generalTagOptions = mergedTagOptions;
    this.selectedGeneralTags = [...new Set(generalTags)];
    this.identifierTagRules = identifierRules;
    this.reconcileIdentifierRuleTags();
    this.cd.detectChanges();
  }

  private extractGeneralTagNames(rules: TaggingRule[]): string[] {
    return rules
      .filter(rule => rule.tagAll)
      .map(rule => rule.name?.trim())
      .filter((name): name is string => !!name);
  }

  private extractIdentifierTagRules(rules: TaggingRule[]): IdentifierTagRule[] {
    return rules
      .filter(rule => !rule.tagAll)
      .map((rule): IdentifierTagRule | null => {
        const tagName = rule.name?.trim();
        const identifiers = this.extractRuleIdentifiers(rule);

        if (!tagName || identifiers.length === 0) {
          return null;
        }

        return {
          id: this.nextIdentifierTagRuleId++,
          tag: tagName,
          currentIdentifier: '',
          identifiers,
        };
      })
      .filter((rule): rule is IdentifierTagRule => !!rule);
  }

  private extractRuleIdentifiers(rule: TaggingRule): string[] {
    const condition = rule.rules?.find(
      conditionItem =>
        conditionItem.variableName === TAGGING_IDENTIFIER_VARIABLE ||
        conditionItem.variableName === LEGACY_TAGGING_IDENTIFIER_VARIABLE
    );

    return this.normalizeUniqueValues(condition?.valueList ?? []);
  }

  private extractActiveTagDefinitionNames(definitions: TagDefinition[]): string[] {
    return definitions
      .filter(definition => !definition.deleted)
      .map(definition => definition.name?.trim())
      .filter((name): name is string => !!name && name.length > 0);
  }

  private normalizeUniqueValues(values: string[]): string[] {
    return [...new Set(values.map(value => value.trim()).filter(Boolean))];
  }

  canSubmitForm(): boolean {
    return this.botSetupForm.valid;
  }

  toggleAudio(controlName: 'welcomeMessage' | 'welcomeMessageoffHours'): void {
    const text = this.botSetupForm.get(`step3.${controlName}`)?.value;
    if (!text) return;

    // Preview what the call will actually say: run the text through the
    // pronunciation dictionary first (server-side, so the FE doesn't duplicate
    // the rewrite). Falls back to the raw text when there is no bot/dictionary.
    this.resolveWelcomeSpokenText(controlName, text).subscribe((spokenText) => {
      this.playWelcomeAudio(controlName, spokenText);
    });
  }

  /**
   * Resolve the dictionary-applied spoken form for a welcome greeting. Uses the
   * welcome-sync endpoint (edit mode + a saved bot) so the preview matches the
   * baked audio; otherwise returns the text unchanged.
   */
  private resolveWelcomeSpokenText(
    controlName: 'welcomeMessage' | 'welcomeMessageoffHours',
    text: string
  ): Observable<string> {
    const botId = this.botToEdit?._id;
    const type = controlName === 'welcomeMessage' ? 'OPEN_HOURS' : 'OFF_HOURS';
    const body = this.buildWelcomeSyncBody();
    if (!botId || this.botToEdit?.type !== 'VOICE' || !body.pronunciationOverrides.length) {
      return of(text);
    }
    return this._botCrudService
      .getWelcomeSyncStatus(botId, {
        items: [{ type, plainText: text }],
        pronunciationOverrides: body.pronunciationOverrides,
      })
      .pipe(
        map(result => result.items.find(item => item.type === type)?.spokenText || text),
        catchError(() => of(text))
      );
  }

  private playWelcomeAudio(controlName: 'welcomeMessage' | 'welcomeMessageoffHours', text: string): void {
    const preset = this.getSelectedVoiceFlavorPreset();
    this._botCrudService.getWelcomeMessageAudio(text, 'mp3', preset?.previewVoice, preset?.previewPrompt).subscribe({
      next: (audioBlob) => {
        const audioUrl = URL.createObjectURL(audioBlob);

        if (controlName === 'welcomeMessage' && this.openHoursAudioRef) {
          const audioEl = this.openHoursAudioRef.nativeElement;
          audioEl.src = audioUrl;

          audioEl.onplay = (): void => {
            this.isPlayingOpenHours = true;
            this.cd.detectChanges();
          };
          audioEl.onpause = (): void => {
            this.isPlayingOpenHours = false;
            this.cd.detectChanges();
          };
          audioEl.onended = (): void => {
            this.isPlayingOpenHours = false;
            this.cd.detectChanges();
          };

          audioEl.play();
        }

        if (controlName === 'welcomeMessageoffHours' && this.offHoursAudioRef) {
          const audioEl = this.offHoursAudioRef.nativeElement;
          audioEl.src = audioUrl;

          audioEl.onplay = (): void => {
            this.isPlayingOffHours = true;
            this.cd.detectChanges();
          };
          audioEl.onpause = (): void => {
            this.isPlayingOffHours = false;
            this.cd.detectChanges();
          };
          audioEl.onended = (): void => {
            this.isPlayingOffHours = false;
            this.cd.detectChanges();
          };

          audioEl.play();
        }
      },
      error: () => {
        console.error('Error obteniendo audio');
      },
    });
  }
}
